import { randomBytes } from 'crypto';
import {
  ENDPOINTS,
  GATE_CONNECTOR_REQUEST_PROTOCOLS,
  GATE_TO_CONNECTOR_PROTOCOLS,
  getLogger,
  makeId,
  tryParse,
  type GameGateData,
  type GameId
} from "@gotti/core";
import { BaseServer, BaseServerConfig, RegisteredServerData } from "../BaseServer.js";
import { resolveAuthIdentity } from "../authIdentity.js";
import { MASTER_GATE_PROTOCOLS, SERVER_TYPES } from "../constants.js";
import { ConnectorServerConfigOptions, ReservedSeat } from "./ConnectorServer.js";
import type { ConnectorChangePayload } from "../types.js";
import { ENCODABLE_AUTH_PROPS, ENCODED_AUTH_PROP_KEYS } from "../state/authRecordEncoding.js";
import type { GateStateStore } from "../state/GateStateStore.js";
import { MemoryGateStateStore } from "../state/MemoryGateStateStore.js";
import { RedisGateStateStore } from "../state/RedisGateStateStore.js";
export type RedisConnectionData = {
  username?: string,
  password?: string,
  keyPath?: string,
  certPath?: string,
  caPath? : string | string[],
  host: string,
  port: number
} | string

export type GateServerConfigOptions = {
  webPort?: number,
  redis?: RedisConnectionData,
  useSequentialPublicIds?: boolean,
  authenticationTimeout?: number,
  seatReservationTimeout?: number,
  /**
   * Namespace every redis key this gate's state store writes (`<prefix>p:<id>`,
   * `<prefix>pubIdMap`, `<prefix>identityMap`). Only meaningful on a
   * redis-backed gate; ignored in the default memory mode.
   *
   * NOTE — this is a KEY-NAMESPACE CHANGE from pre-store releases, which wrote
   * the same three keys UNPREFIXED (`p:<id>`, `pubIdMap`, `identityMap`). A
   * redis-backed deployment upgrading across this change starts from an empty
   * namespace (the spec's clean-redeploy story); set `redisKeyPrefix: ''` to
   * keep reading the legacy unprefixed keys.
   */
  redisKeyPrefix?: string,
}

/** Default namespace for a redis-backed gate's keys — see `redisKeyPrefix`. */
const DEFAULT_REDIS_KEY_PREFIX = 'gotti:';

export type GateServerConfig = Omit<BaseServerConfig<GateServerConfigOptions>, 'type'>

export type AuthenticatedGateClient<Auth={}> = {
    auth: Auth,
    privateId: string,
    publicId: string | number,
    time: number,
    givenGameList?: GameGateData[],
    connectorId?: string,
    /**
     * NOTE — the areas this client is in are NOT a field on this record. They
     * live in the state store's own client→area index
     * (`addClientArea`/`removeClientArea`/`getClientAreas`/`clearClientAreas`),
     * one row per area, keyed by privateId. They used to be an `areas` array
     * on this record, JSON-encoded into the record blob on a redis-backed
     * gate — a read-modify-write of the WHOLE list per report, which is
     * exactly the write path a lost update (and the C1 encode bug) lived on.
     * `holdMsFor`/`rejoinAreaIdsFor`/`consumeRejoinAreas` read the index.
     */
    /** The game this client's held areas belong to — a rejoin is scoped to it. */
    gameId?: GameId,
    /**
     * The seat-hold window this client was last HANDED (`consumeRejoinAreas`),
     * kept only until its connector's own area reports refill the client→area
     * index.
     *
     * The index is emptied the moment the seat is granted, but the connector
     * cannot report the joins that refill it until the client actually
     * connects. Without this, `holdMsFor` reads 0 for that whole gap and a
     * disconnect inside it purges the record — recycling a publicId that an
     * area is still holding a seat for. Cleared by the first area report,
     * which is the authoritative answer it stands in for.
     */
    heldGraceMs?: number,
    /**
     * The `identityMap` key this record owns (`gameId` + the verified identity
     * value), written at join time. Stored on the record rather than re-derived
     * on purge because `gameId` is only written once `reserveSeat` SUCCEEDS: a
     * record purged before that could not compute its own key, and leaked its
     * index entry forever.
     */
    identityKey?: string,
}

// `ENCODABLE_AUTH_PROPS`/`ENCODED_AUTH_PROP_KEYS` moved to
// `../state/authRecordEncoding` (see that file's doc comment for why) —
// re-exported here for backward compatibility with existing importers.
export { ENCODABLE_AUTH_PROPS, ENCODED_AUTH_PROP_KEYS };

import * as http from 'http';
const { GATE, MASTER, CONNECTOR } = SERVER_TYPES;

export abstract class GateServer<Auth={}> extends BaseServer {
	readonly logger = getLogger("GateServer:", { kind: 'gate', serverId: this.id });

	readonly authenticationTimeouts?: {[clientId: string]: any } = {};
	readonly seatReservationTimeouts?: {[clientId: string]: any } = {};

	readonly games : Array<GameGateData> = [];
	readonly connectors : Array<RegisteredServerData<ConnectorServerConfigOptions>> = [];
	readonly master : Array<RegisteredServerData<any>> = [];
	readonly reservingSeats : {[k: string]: RegisteredServerData<ConnectorServerConfigOptions> } = {};
	readonly pendingSeatReservations : {[connector: string]: number } = {};

	/**
	 * The grace TIMERS this gate owns — nothing else. Clients whose connector
	 * died, or who cleanly disconnected from a still-healthy connector, have
	 * their auth record held (not purged) for the client's own hold window
	 * (`holdMsFor` — the longest seat-hold among the areas it is in). Armed/
	 * re-armed by `graceHold`, cleared by a successful re-reservation
	 * (`reserveSeat`'s `clearPendingReconnect`) or by grace expiry itself (the
	 * timer deletes its own entry), or dropped outright by
	 * `clearAllPendingReconnects` on `close()`.
	 *
	 * The HOLD ITSELF — which connector is holding a reconnect window open for
	 * this privateId — is cluster-visible state and lives in the store
	 * (`setGraceHold`/`getGraceHold`/`clearGraceHold`); this Map used to carry
	 * that `connectorId` alongside the timer. Only the timer is left, because a
	 * timer is inherently per-PROCESS: the gate that armed it is the only one
	 * that can fire it, and it dies with the window it belongs to (allowed
	 * per-client residue under the memory-bounded rule — bounded by the clients
	 * THIS gate is currently holding, not by the cluster's session count). A
	 * redis-backed gate additionally gets a TTL backstop on the store record so
	 * a gate that dies mid-hold cannot leave an immortal one behind.
	 */
	private holdTimers: Map<string, ReturnType<typeof setTimeout>> = new Map();

	/**
	 * privateIds with a `reserveSeat` call currently awaiting its connector's
	 * RESERVE_SEAT response (armed right before that request, cleared right
	 * after it settles — see `reserveSeat`). Consulted by `graceHold`'s purge
	 * timer: purging a privateId's auth while its reservation is in flight
	 * would race that reservation's own post-response `updateUserAuth` call —
	 * if the reservation then succeeds, it would be writing onto a deleted
	 * record (TypeError) after the connector already granted the seat.
	 */
	private reservationsInFlight: Set<string> = new Set();

	/**
	 * privateIds whose grace-hold purge fired while `reservationsInFlight`
	 * held them, and was therefore deferred instead of run immediately (see
	 * `graceHold`). The in-flight reservation's own outcome resolves the
	 * deferral: success clears the entry with no purge (the auth record was
	 * never touched, so there's nothing to undo); failure re-checks this set
	 * in `reserveSeat`'s catch block and purges then, so a reservation that
	 * never lands doesn't leave an immortal auth record behind.
	 */
	private deferredPurges: Set<string> = new Set();

	/**
	 * Connector ids the master has flagged as draining (CONNECTOR_CHANGE
	 * op:'draining') — excluded from both `getAvailableConnectors()`
	 * (`getHealthyConnectorById` reads through it) and
	 * `getAvailableConnectorsWithArea()` (`getBestConnector`'s first-choice
	 * filter, which it tries before falling back to `getAvailableConnectors()`)
	 * so neither a fresh join nor a reconnecting client's own previous
	 * connector is ever handed a connector that is on its way out. `removed`
	 * deletes the entry too, so a connector that finishes draining and fully
	 * disconnects doesn't leak a permanent id in this set.
	 */
	private drainingConnectors: Set<string | number> = new Set();

	/**
	 * privateId → the tail of that client's in-flight area-report chain. Purely
	 * a serialization latch for `handleClientAreaChanged`'s read-modify-write of
	 * the client→area index; see the comment there for what interleaving costs.
	 * Entries are deleted as the chain drains.
	 */
	private areaReportQueues: Map<string, Promise<void>> = new Map();

	/**
	 * The recycled-publicId pool for `useSequentialPublicIds`.
	 *
	 * This array is the MEMORY-MODE BACKING ONLY, exactly like
	 * `authenticatedClients` and `identityMap` above: `MemoryGateStateStore`
	 * wraps this instance, so a memory gate's `releaseSequentialId`/
	 * `acquireSequentialId` push and shift these very entries and reading
	 * `gate.unusedSequentialIds` still shows the live pool. A redis-backed gate
	 * keeps the pool in the `<prefix>seqIds` LIST instead — an atomic
	 * `RPUSH`/`LPOP` pair, which is the whole point: two gates popping
	 * concurrently can never be handed the same id, whereas a per-process array
	 * would let each gate mint the same publicId for two different players.
	 * This array therefore stays permanently EMPTY on a redis gate.
	 *
	 * ORDERING (memory mode): the pool is FIFO now — `acquireSequentialId`
	 * shifts, where `makePublicId` used to `pop()`. The store's contract is FIFO
	 * on both backings (redis `RPUSH`/`LPOP`), and a LIFO memory pool would have
	 * been a second, silently different recycling order. Which recycled id comes
	 * back first carries no meaning — the pool exists so ids stay small and
	 * bounded, not to order them.
	 */
	readonly unusedSequentialIds : Array<number> = [];
	private lastPublicIdSeq : number = 1;

private httpServer : any;

	readonly authenticatedClients : {[clientId: string]:  AuthenticatedGateClient<Auth> } = {};

	/**
	 * Verified identity value → privateId of the session that owns it. This is
	 * what makes a rejoin survive a page reload: `privateId` is minted per
	 * session and tells us nothing across one, but the identity comes from the
	 * auth the gate itself verified.
	 *
	 * This Map is the MEMORY-MODE backing only: `MemoryGateStateStore` wraps
	 * this exact instance, so reading `gate.identityMap` and going through
	 * `setIdentity`/`getIdentityOwner`/`clearIdentity` see the same entries. A
	 * redis-backed gate keeps the index in the `<prefix>identityMap` hash
	 * INSTEAD and leaves this Map empty — it used to be mirrored into as well,
	 * but nothing ever read the mirror (see the note on those three methods).
	 *
	 * Written ONLY at join time (`handleJoinGame`), because the identity path is
	 * declared per GAME and `checkAuth` has no gameId to resolve it against.
	 * Cleared when the record that owns it is purged (`removeUserAuth`).
	 *
	 * KEYED PER GAME (`identityKeyFor`), not on the identity value alone. Each
	 * game declares its OWN `authIdentityPath`, so two games on one gate draw
	 * their identities from independent value spaces: game 2's `userId: 42` and
	 * game 1's `accountId: 42` are different players who happen to share a
	 * value. On a global namespace the second one to join MATCHES the first —
	 * evicting a stranger's transport and adopting their in-game id — and a
	 * player joining a second game with a fresh `authenticate()` silently
	 * evicts their own first-game session.
	 */
	readonly identityMap: Map<string, string> = new Map();

	/**
	 * The memory backing, built once and kept: unlike `RedisGateStateStore` it
	 * is NOT stateless — it owns the `publicId -> privateId` ownership index
	 * (`pubIdOwners`), which is per-gate state that must survive across
	 * `stateStore` reads. Constructed lazily by the getter below rather than as
	 * a field initializer so it can never observe `authenticatedClients`/
	 * `identityMap` before their own initializers have run.
	 */
	private memoryStateStore?: MemoryGateStateStore;

	/**
	 * The redis store, memoized on the IDENTITY of the `this.redis` handle it
	 * wraps — not unconditionally. `RedisGateStateStore` is stateless (a handle
	 * plus a key prefix), so re-using one is free of the mode-flip hazard the
	 * memory memoization has to avoid; but the getter runs on every auth read
	 * and write, and this method's own call sites now await several store ops
	 * per area report, so allocating a fresh wrapper per access is pure waste.
	 * Keyed on the handle so a gate that (re)connects redis — `initialize()`
	 * assigns `this.redis` after construction — never keeps a store pointing at
	 * a dead client.
	 */
	private redisStateStore?: { redis: any, store: RedisGateStateStore };

	/**
	 * Every piece of this gate's auth bookkeeping — the auth records, the
	 * publicId ownership index and the identity ownership index — is read and
	 * written through here, so `GateServer` itself has exactly ONE
	 * redis-or-memory decision (this getter) instead of the ten inline
	 * `this.redis` forks it used to carry.
	 *
	 * Resolved per access, deliberately, rather than assigned once at init:
	 * `this.redis` is created inside `BaseServer.initialize()` (after
	 * `onBeforeInit`, before `onAfterInit`), but a large part of this codebase —
	 * and every `GateServer` unit test — drives a gate that never calls
	 * `initialize()` at all. A getter is correct in both worlds and needs no
	 * subclass to remember to call `super.onAfterInit()`.
	 *
	 * `RedisGateStateStore` is cheap and stateless (a redis handle + a key
	 * prefix), so it always tracks the CURRENT `this.redis` — memoized on that
	 * handle's identity (see `redisStateStore`), rebuilt the moment it changes;
	 * the memory store is memoized because it owns state.
	 */
	public get stateStore(): GateStateStore {
		if(this.redis) {
			if(this.redisStateStore?.redis !== this.redis) {
				this.redisStateStore = {
					redis: this.redis,
					store: new RedisGateStateStore(this.redis, { keyPrefix: this.config.options?.redisKeyPrefix ?? DEFAULT_REDIS_KEY_PREFIX }),
				};
			}
			return this.redisStateStore.store;
		}
		return (this.memoryStateStore = this.memoryStateStore ?? new MemoryGateStateStore({
				authenticatedClients: this.authenticatedClients,
				identityMap: this.identityMap,
				seqIds: this.unusedSequentialIds,
				// By accessor, not by reference — see `makePublicId`. This keeps
				// `lastPublicIdSeq` the live mint counter in memory mode, so
				// behaviour (and anything that pokes the field) is unchanged.
				seqCounter: { get: () => this.lastPublicIdSeq, set: (v: number) => { this.lastPublicIdSeq = v; } },
			}));
	}

	declare readonly config: BaseServerConfig<GateServerConfigOptions>;
	constructor(c : GateServerConfig) {
		super({ ...c, type: GATE });
		this.onConnectionMade(d => {
			if(d.type === MASTER) {
				this.masterServer = d;
			}
		});
		this.onConnectionClosed(d => {
			if(d.type === MASTER && this.masterServer?.id === d.id) {
				this.masterServer = undefined;
			} else if(d.type === CONNECTOR) {
				// Fire-and-forget (this listener is sync), but never an unhandled
				// rejection: on a redis-backed gate the hold sizing inside is a
				// network read, and an unhandled rejection is process-fatal on
				// modern Node.
				this.handleConnectorClosed(d).catch(err => {
					this.logger.error(`Failed to grace-hold clients of closed connector ${d.id}: ${err}`);
				});
			}
		});
			/*
			setInterval(() => {
					Object.keys(this.authenticatedClients).forEach(clientId => {
							const client = this.authenticatedClients[clientId];
							if(client.connector && Date.now() - client.time > 60000 * 60 * 3) {
									this.requestServer(client.connector.id, [GATE_CONNECTOR_REQUEST_PROTOCOLS.CHECK_CLIENT, client.privateId]).then(stilActive => {
											if(!stilActive) {
													delete this.authenticatedClients[clientId];
											} else {
													client.time = Date.now();
											}
									})
							} 
					});
			}, 10000);
			*/
	}

	onRemovedAuth?(client: AuthenticatedGateClient<Auth>) : Promise<void>
	onAuthorize?(request: http.IncomingMessage, body?: any) : Promise<AuthenticatedGateClient<Auth>>
	onGetGames?(client: AuthenticatedGateClient<Auth>, clientRequestOptions: any) : Promise<Array<GameGateData>> | Array<GameGateData>
	abstract onAuthenticate(opts: any, req: http.IncomingMessage) : Promise<Auth> | Auth
	abstract onJoinGame(client: AuthenticatedGateClient<Auth>, opts?: any) : Promise<GameId | GameGateData> | GameId | GameGateData

	// ── Dynamic area instances — matchmaking relay to the master ─────────────
	// Use these inside onAuthenticate/onJoinGame/onGetGames to matchmake: check
	// capacity, create-or-join an instance, and route the client accordingly.
	// The master authorizes these because they arrive over its registered gate
	// connection (same trust as seat reservation).

	private masterServer? : RegisteredServerData<any>;

	private requestMaster(payload: any) : Promise<any> {
		if(!this.masterServer) {
			throw new Error(`Gate is not connected to a master server.`);
		}
		return this.requestServer(this.masterServer.id, payload);
	}

	/** Create-or-reuse a dynamic area instance (idempotent on `areaKey`). */
	public createOrJoinArea(type: string, opts: { gameId?: string, areaKey?: string, configData?: any, policy?: any } = {}) : Promise<{ areaId: string, created: boolean }> {
		return this.requestMaster([MASTER_GATE_PROTOCOLS.CREATE_DYNAMIC_AREA, type, opts]);
	}

	/** Tear down a dynamic area instance. */
	public tearDownDynamicArea(areaId: string, opts: { drain?: boolean, reason?: string } = {}) : Promise<void> {
		return this.requestMaster([MASTER_GATE_PROTOCOLS.DESTROY_DYNAMIC_AREA, areaId, opts]);
	}

	/** Running / capacity / available for `type`, per host and in total. */
	public getAreaCapacity(type: string) : Promise<{ type: string, total: { running: number, capacity: number, available: number }, byHost: Array<{ hostServerId: string | number, running: number, capacity: number, available: number }> }> {
		return this.requestMaster([MASTER_GATE_PROTOCOLS.GET_AREA_CAPACITY, type]);
	}

	/** Live instance records, filterable by type/gameId. */
	public listDynamicAreas(filter?: { type?: string, gameId?: string }) : Promise<any[]> {
		return this.requestMaster([MASTER_GATE_PROTOCOLS.LIST_DYNAMIC_AREAS, filter]);
	}

	public getAvailableConnectorsWithArea(area: string) {
		return Object.values(this.serverConnections).filter(v => {
      if(v.type !== CONNECTOR) return false;
      // Same chokepoint rule as getAvailableConnectors: a draining connector
      // is never handed out for a new/re seat. getBestConnector tries THIS
      // filter first (falling back to getAvailableConnectors only if it comes
      // back empty), so without this check here too a draining connector with
      // no maxClients configured (the common case below) would leak straight
      // back through the "return true" short-circuit on the next line.
      if(this.drainingConnectors.has(v.id)) return false;
			const { clientCount, areas, maxClients } = v.heartbeats.lastState || {};
      if(maxClients === undefined || maxClients === -1) return true;
			return Number.isInteger(clientCount) && clientCount < maxClients && areas.includes(area);
		});
	}

	public async handleJoinGame(authToken: string, data: any, req: http.IncomingMessage) : Promise<{
		host: string,
		port: number,
		gameId: string | number
		gameType: string | number,
		gameData: any,
		connectorType : 'websocket' | 'webRtc' | 'udp',
		/**
		 * The client's in-game id for THIS join — normally the one `checkAuth`
		 * minted, but the id of the RESUMED session when this authentication was
		 * identity-matched to a held one. The client must adopt it before
		 * connecting: `ConnectorServer.onValidateClient` compares the publicId the
		 * client sends against the reserved seat's.
		 */
		publicId: string | number,
	}> {
			// console.log('IN JOIN GAME')
			// console.log('IN GOTTI_JOIN_GAME', body)
			if(!authToken && !this.onAuthorize) throw new Error(`Expected auth token...`);
			const client = this.onAuthorize ? await this.onAuthorize(req, data) : await this.getUserAuth(authToken)
			// console.log('client was:', client);
			if(!client) throw new Error(`Unauthorized client.`);
			const gameOrGameId = await this.onJoinGame(client, data);
			// console.log('on join hame id:', gameId);
			if(!gameOrGameId) throw new Error(`onJoinGame`);
      const gameId : GameId = typeof gameOrGameId === 'object' ? gameOrGameId.id : gameOrGameId;
			const game = this.games.find(g => g.id === gameId);
			if(!game) throw new Error(`Did not find game for gameId: ${gameId} Make sure your gate onJoinGame method returns a valid gameId`);
			if(typeof gameOrGameId === 'object' && gameOrGameId !== game) {
        throw new Error(`onJoinGame did not return the expected game object... if you are trying to override the return data, this is not the way to do it. Preferably do this in the onAuthenticate or onJoinRequest hooks.`);
      }

			// Identity matching, BEFORE reserveSeat — reserveSeat derives the
			// server-side rejoin list (rejoinAreaIdsFor) and the hold window from
			// this record's held-area index and `gameId`, which the resume is what
			// re-keys and populates.
			// Reordering these silently downgrades every resumed session to a
			// fresh join.
			const identityPath = this.identityPathFor(gameId);
			const identity = resolveAuthIdentity(client.auth, identityPath);
			if(identity) {
				// Scoped to the game being joined — the same identity VALUE under a
				// different game is a different player (see `identityMap`).
				const identityKey = this.identityKeyFor(gameId, identity);
				const resumedPublicId = await this.resumeSession(client, identityKey);
				if(resumedPublicId === undefined) {
					// Nothing to resume — this identity's first join (or its previous
					// session is gone). Record the live owner so the NEXT join can
					// match it.
					await this.claimIdentity(client, identityKey);
				}
			} else if(identityPath) {
				// Declared a path, but this client's verified auth carries no value
				// at it (anonymous/guest auth, a claim the identity provider did not
				// return, ...). That is "no durable identity": normal join, no
				// resume. NEVER fall back to privateId — that is per-session, and
				// matching on it is exactly what cannot survive a page reload.
				this.logger.warn(`Game ${gameId} declares authIdentityPath '${identityPath}' but this client's verified auth has no value there — joining as a NEW session (no seat resume).`);
			}

      // console.log('reserve seat', client.privateId, gameId, data)
			const reserved = await this.reserveSeat(client.privateId, gameId, data);
			// console.log('SUCCESFULLY reserved', reserved);
			let host, port;
			let connectorType = 'websocket';
			if('webRtcOptions' in reserved.connector.options) {
				if(!reserved.connector.options.websocketOptions){
					throw new Error(`WebRTC connectors still need websocket options for the signaling process.`);
				}
				connectorType = 'webRtc';
				({ host, port } = reserved.connector.options.websocketOptions);
				//console.log('wrtc host port', { host, port })
			} else if('websocketOptions' in reserved.connector.options) {
				({ host, port } =  reserved.connector.options.websocketOptions);
			} else if ('udpOptions' in reserved.connector.options) {
				connectorType = 'udp';
				({ host, port } =  reserved.connector.options.udpOptions);
			} else {
				throw new Error(`Connector server does not have a valid connection type.`);
			}
			return {
				host,
				port,
				gameId,
				gameType: reserved.gameType,
				gameData: game.clientData,
				connectorType: connectorType as ConnectorServerConfigOptions['protocolType'],
				// The seat was reserved under this publicId — the client has to send
				// the same one or the connector rejects the connection.
				publicId: reserved.publicId,
			}
	}

	/**
	 * Runs AFTER `BaseServer.initialize()` created `this.redis`, which is the
	 * earliest point the `stateStore` getter can answer truthfully — the same
	 * ordering constraint that getter documents.
	 *
	 * ONE LINE, and it is the highest-leverage line in the redis work. This
	 * feature's worst failure mode is a SILENT DOWNGRADE — a gate that was
	 * supposed to share its auth records, seat reservations, grace holds and
	 * publicId pool with the rest of the cluster running its own private
	 * in-process copies instead. Every symptom of that is remote and confusing
	 * (a reconnect that lands on another gate is treated as a fresh join; two
	 * gates mint the same sequential publicId), and it has already happened
	 * three independent ways during this work — a dropped key prefix, a `redis`
	 * config option that had to stay where it was precisely because removing it
	 * would silently downgrade existing consumers, and deploy-time host-string
	 * scoping. None of them said anything at boot. This does, and the PREFIX is
	 * in it because a wrong prefix is the same failure wearing a disguise: the
	 * gate really is on redis, in a namespace no other gate shares.
	 *
	 * A subclass that overrides this must call `super.onAfterInit()`, or its
	 * gate boots silent again.
	 */
	public override async onAfterInit() {
		this.logger.info(this.redis
			? `state store: redis (prefix=${this.config.options?.redisKeyPrefix ?? DEFAULT_REDIS_KEY_PREFIX})`
			: `state store: memory (this gate's auth records, seats, grace holds and publicId pool are PRIVATE to this process)`);
	}

	public override async onBeforeInit() {
		return new Promise<void>((resolve, reject) => {
			this.httpServer = http.createServer(async (req, res) => {
				res.setHeader('Access-Control-Allow-Origin', '*');
				res.setHeader('Access-Control-Allow-Methods', 'GET, POST, OPTIONS');
				res.setHeader('Access-Control-Allow-Headers', 'Content-Type, Authorization');

				const authToken = req.headers.authorization;
				const endpoint = req.url.startsWith('/') ? req.url.substring(1) : req.url;
				const method = req.method === 'OPTIONS' ? req.headers['access-control-request-method'] : req.method;

				if(!ENDPOINTS[method] || !Object.values(ENDPOINTS[method])?.includes(endpoint)) {
					res.writeHead(404, { 'Content-Type': 'text/plain' });
					res.end('Not Found');
					return;
				}
				try {
					if (req.method === 'OPTIONS') {
						res.writeHead(200, {
							'Content-Type': 'application/json',
							'Access-Control-Allow-Methods': 'GET, POST, OPTIONS',
							'Access-Control-Allow-Headers': 'Content-Type, Authorization'
						});
						res.end();
						return;
					}
				
					if(req.method === 'POST') {
						let body = '';
						req.on('data', (chunk) => {
								body += chunk;
						});
						req.on('end', async () => {
							try {
								const data = tryParse(body);
								switch(endpoint) {
									case ENDPOINTS.POST.GOTTI_AUTHENTICATE: {
										// console.log('chekcing auth...', data)
										const auth = await this.checkAuth(data, req);
									//  console.log('confirmed auth', auth);
										res.writeHead(200, { 'Content-Type': 'application/json' });
										res.end(JSON.stringify(auth));
										break;
									}
									case ENDPOINTS.POST.GOTTI_JOIN_GAME: {
										const joinData = await this.handleJoinGame(authToken, data, req);
										res.writeHead(200, { 'Content-Type': 'application/json' });
										res.end(JSON.stringify(joinData));
										break;
									}
									case ENDPOINTS.POST.GOTTI_GAMES_LIST: {
										if(!authToken && !this.onAuthorize) throw new Error(`Expected auth token...`);
										const client = this.onAuthorize ? await this.onAuthorize(req) : await this.getUserAuth(authToken)
										if(!client) throw new Error(`Unauthorized client.`);
										const givenGameList = await this._getGames(client, data);
										await this.updateUserAuth(client.privateId, 'givenGameList', givenGameList);
										res.writeHead(200, { 'Content-Type': 'application/json' });
										res.end(JSON.stringify(givenGameList));
										break;
									}
								}
							} catch (innerError: any) {
								res.writeHead(400, { 'Content-Type': 'application/json' });
								res.end(JSON.stringify({ message: innerError.message }));
							}  
						});
					} else {
						throw new Error(`Gate server is only handling post requests.`)
					}
				} catch (error) {
					res.writeHead(400, { 'Content-Type': 'application/json' });
					res.end(JSON.stringify({ message: error.message }));
				}
			});
			const port = this.config.options?.webPort || 3040;
			// console.log('GATE IS LISTENING ON PORT', port)
			// `bindHost` set → that interface only; unset → every interface, the
			// gate's listen since before `bindHost` existed (a default of `host`
			// would have quietly made existing gates loopback-only).
			const onListening = () => {
				this.httpServer.off('error', reject);
				resolve();
			};
			// A listen that fails (port taken, a bindHost this machine does not
			// have) FAILS THE BOOT. It used to be logged by the handler below and
			// never settle, so initialize() hung forever with nothing listening.
			this.httpServer.once('error', reject);
			if(this.config.bindHost) {
				this.httpServer.listen(port, this.config.bindHost, onListening);
			} else {
				this.httpServer.listen(port, onListening);
			}
			this.httpServer.on('error', (err: any) => {
				console.error('GATE SERVER error:', err)
			});
		})
	}

	private async _getGames(client: AuthenticatedGateClient<Auth>, clientRequestBody: any) : Promise<GameGateData[]> {
		if(this.onGetGames) {
			return this.onGetGames(client, clientRequestBody)
		} else {
			return [...this.games];
		}
	}


	private async removeUserAuth(privateId: string) {
		if(privateId in this.authenticationTimeouts) {
			clearTimeout(this.authenticationTimeouts[privateId]);
			delete this.authenticationTimeouts[privateId];
		}
		if(privateId in this.seatReservationTimeouts) {
			clearTimeout(this.seatReservationTimeouts[privateId]);
			delete this.seatReservationTimeouts[privateId];
		}
		const auth = await this.getUserAuth(privateId);
		if(!auth) {
			console.warn(`Did not find auth for privateId: ${privateId}`)
			return false;
		}
		// This record is going away for good (grace expired, deliberate leave,
		// authentication timeout) — release the identity it owns so the same
		// player's NEXT authentication is a genuinely fresh session rather than a
		// resume of a seat nobody is holding any more. Guarded on still being the
		// owner: a resumed session already re-pointed the identity at its own
		// privateId, and this record must not steal that entry back out from under
		// it when it is retired.
		//
		// Read off the RECORD (`identityKey`, written by `claimIdentity` at
		// join time) rather than re-resolved from `auth.gameId`: gameId is only
		// written once `reserveSeat` SUCCEEDS, so a record purged before that
		// (a reservation that threw, an authentication timeout) had no way to
		// name its own key and leaked its `identityMap` entry permanently.
		const identityKey = auth.identityKey;
		if(identityKey && (await this.getIdentityOwner(identityKey)) === privateId) {
			await this.clearIdentity(identityKey);
		}
		// PARITY (Task 3): the two branches this replaced guarded the sequential
		// release differently — the redis one released only `if(publicId)`, the
		// memory one released unconditionally (pushing `parseInt(undefined)` =
		// NaN for a record with no publicId).
		//
		// NEITHER form survives: the pool is the store's now, and a NaN in it is
		// not a curiosity — `acquireSequentialId` hands it straight back as the
		// next player's publicId (`NaN != null`), and on a redis gate it is
		// PERSISTED, so one bad release poisons every gate in the cluster. So the
		// release is guarded on the value actually being a usable id, which is
		// the RELEASE half of the same guard `makePublicId`'s acquire uses —
		// `Number.isFinite`, so `0` still releases (a legitimate id) while
		// `undefined`/`''`/a non-numeric publicId does not.
		if(this.config.options.useSequentialPublicIds) {
			const released = parseInt(auth.publicId as string);
			if(Number.isFinite(released)) {
				await this.stateStore.releaseSequentialId(released);
			}
		}
		// UNCONDITIONAL now, where only the redis branch used to do it: memory
		// mode keeps a real publicId ownership index inside the store (it used
		// to derive ownership by scanning `authenticatedClients`, which a
		// deleted record answered for free), so failing to release here would
		// leave the index pointing at a purged privateId forever.
		//
		// `!= null`, not truthiness (was `if(publicId)`): this is the RELEASE
		// half of the ownership guard, and `setUserAuth`'s TAKE half is guarded
		// the same way, so the two can never disagree about whether this record
		// owns a key. Under the old asymmetric pair a record with a falsy
		// publicId (`0`, `''`) took ownership and was never released — see
		// `setUserAuth`.
		const publicId = auth?.publicId;
		if(publicId != null) {
			try {
				await this.stateStore.deletePublicIdOwner(publicId);
			} catch (err) {
				console.error('error removing pubIdMap', err);
			}
		}
		// The record names the connector this client is seated on, and this is
		// the disconnect-purge that ends that seat — so the connector→clients
		// index entry goes with it. Read off the record BEFORE the delete below
		// (afterwards there is nothing left to name the connector), and released
		// unconditionally: leaving it behind would keep a dead privateId in a
		// connector's set forever, and `handleConnectorClosed` would then try to
		// grace-hold a record that no longer exists on every connector death.
		if(auth.connectorId != null) {
			await this.stateStore.removeClientFromConnector(auth.connectorId, privateId);
		}
		await this.stateStore.deleteAuthRecord(privateId);
		// The record is gone for good, so its client→area rows must go with it.
		// They used to BE the record (an `areas` field), so deleting it took the
		// index along; now the index is separate state and needs its own delete
		// — without this, a purged client leaves rows behind forever (a redis
		// key with no TTL, a Map entry in memory mode) and `graceHold`'s
		// "the purge is what keeps the index from going stale" contract breaks.
		await this.stateStore.clearClientAreas(privateId);
		// Same rule, same reason, for the other two per-client store records this
		// gate writes. Each has a path that can outlive the record it belongs to:
		// `graceHold`'s immediate-purge branch does not disarm a hold a previous
		// signal armed, and a reservation initiated on ANOTHER gate leaves a
		// reserving record this gate's `removeSeat` skips (its local guard is
		// empty here). privateIds are never reused, so anything left behind is
		// addressed by nothing and deleted by nothing — an immortal key per dead
		// session on redis. The purge is the one chokepoint every ending session
		// passes through, so it is where they are released.
		//
		// NOT the hold TIMER: this method can run while another path's timer is
		// still armed, and that timer owns its own map entry (it deletes it on
		// fire, then finds no record and no-ops) exactly as it did when the hold
		// WAS that map entry.
		await this.stateStore.clearGraceHold(privateId);
		await this.stateStore.clearReservingSeat(privateId);
		await this.onRemovedAuth?.(auth)
		return true;
	}

	/**
	 * Deliberately NOT `async`: the store's promise is returned DIRECTLY rather
	 * than adopted by an async wrapper, so `await this.getUserAuth(...)` still
	 * settles in exactly ONE microtask — the way the old synchronous memory
	 * branch did. An `async` wrapper around a promise-returning callee costs two
	 * extra ticks per read, which is enough to reorder the purge-vs-reservation
	 * races `GateServer.reconnect.test.ts` drives by counting microtasks.
	 */
	private getUserAuth(privateId: string) : Promise<AuthenticatedGateClient<Auth> | undefined> {
		return this.stateStore.getAuthRecord(privateId) as Promise<AuthenticatedGateClient<Auth> | undefined>;
	}

	private async setUserAuth(privateId: string, data:AuthenticatedGateClient<Auth>){
		await this.stateStore.setAuthRecord(privateId, data);
		// The SECOND of the two writes this method has always made — kept a
		// separate store call rather than folded into `setAuthRecord`, because
		// that is exactly the shape the redis branch had (two independent
		// `hset`s) and `GateStateStore` deliberately keeps them independent.
		//
		// UNCONDITIONAL now, where only the redis branch used to do it. Memory
		// mode has a real ownership index behind `getPublicIdOwner` instead of
		// the old scan over `authenticatedClients`, so a record persisted
		// without this call would simply never resolve by publicId. Both of this
		// method's callers rely on it: `checkAuth`'s mint, and `resumeSession`'s
		// re-persist (which is what re-points the ADOPTED publicId at the fresh
		// privateId).
		//
		// GUARDED, symmetrically with `removeUserAuth`'s release: a record with
		// no publicId owns no ownership key. Ungated, this took ownership under
		// the literal key `'undefined'` for such a record, and `removeUserAuth`
		// (whose release is publicId-conditional) could never release it — a
		// permanent index entry pointing at a purged privateId, resolvable by
		// `getClientByPublicId('undefined')`. Unreachable on real paths
		// (`checkAuth` always mints one first), which is exactly why it needed
		// fixing at BOTH ends rather than being argued away at one.
		if(data.publicId != null) {
			await this.stateStore.setPublicIdOwner(data.publicId, data.privateId);
		}
	}

	private async updateUserAuth(privateId: string, key: string, data: any){
		// Belt-and-braces: a record that's gone by the time a caller gets here
		// (e.g. purged between that caller's own earlier read and this write —
		// reserveSeat's primary defense against that specific race is
		// reservationsInFlight/deferredPurges above, but this guard covers any
		// other path, present or future) must degrade to a silent no-op, not a
		// TypeError writing a property onto `undefined`. Every current caller
		// (the GOTTI_GAMES_LIST handler's givenGameList write, reserveSeat's
		// connectorId and gameId writes, applyClientAreaChange's and
		// consumeRejoinAreas' heldGraceMs writes, claimIdentity's identityKey
		// write) already tolerates the write silently not landing. (The area
		// list is no longer among them — it is its own index in the store, not
		// a field on this record.)
		//
		// `data` must never be `undefined`: on a redis-backed gate this is an
		// `hset` field value, and node-redis' RESP2 encoder throws
		// `TypeError: Invalid argument type` on anything that is not a string or
		// a Buffer. Callers pass a concrete value (`[]`, `0`, an id), never an
		// optional read straight off another record — see `resumeSession`.
		// The record-still-exists guard, the `publicId` ownership re-point and
		// the encoded-prop stringification all live inside the store's
		// `updateAuthField` now (both backings) — the gone-record case is part
		// of that op's documented contract, so the guard is not repeated here.
		//
		// PARITY (Task 5): `updateAuthField` also skips an `undefined` value on
		// BOTH backings. The old memory branch wrote it (`record[key] =
		// undefined`); the old redis branch would have thrown in node-redis'
		// RESP2 encoder. No caller passes `undefined` today (see the note
		// above), so the two agree in practice.
		await this.stateStore.updateAuthField(privateId, key, data);
	}

	/**
	 * The connector says this client joined/left an area. The gate keeps the
	 * list because it — not master, and never the client — is the source of
	 * truth for where a reconnecting player gets put back.
	 *
	 * `reportingConnectorId` is the connection the report actually arrived on,
	 * and a report is only honoured from the connector this client is CURRENTLY
	 * seated on. Without that check a dying connector's late `joined:false` —
	 * emitted after the client already re-seated elsewhere — would drop a LIVE
	 * area from the index: no rejoin back into it, and a shorter `holdMsFor`,
	 * so the record can be purged while the area is still holding the seat.
	 */
	private async handleClientAreaChanged(reportingConnectorId: string | number, privateId: string, areaId: string | number, joined: boolean, graceMs: number) {
		// SERIALIZED PER CLIENT. The index write itself is now a single atomic
		// store op (one row per area — `addClientArea`/`removeClientArea`), so
		// the whole-list lost update this queue was originally built for cannot
		// happen any more even cross-process. The queue STAYS because it also
		// serializes the rest of `applyClientAreaChange` against itself: the
		// connector-ownership check (`getUserAuth` -> compare connectorId) and
		// the hold-window zeroing (`heldGraceMs`) still straddle awaits, and
		// reports for one client genuinely do arrive back-to-back in a single
		// message pump tick — a rejoin joins EVERY held area from one
		// GET_FIRST_AREAS burst, and `replaceClientArea` emits the new area's
		// join and the old area's leave together. Historic teeth: with the old
		// read-modify-write of an `areas` array on the record, a two-area rejoin
		// ended up indexed under ONE area (so the next reconnect silently
		// abandoned the other) and a `replaceClientArea` kept the area it had
		// just left (a phantom seat restored) — a deterministic lost update, not
		// a rare interleaving. The concurrent-report tests in
		// GateServer.clientAreas.test.ts still pin the observable behaviour.
		//
		// The queue is per privateId, so unrelated clients still process in
		// parallel, and it is dropped as soon as it drains (no unbounded growth).
		// A rejected link must not poison the chain — every link is caught here,
		// and `applyClientAreaChange`'s own writes are already fail-soft.
		const previous = this.areaReportQueues.get(privateId) ?? Promise.resolve();
		const current = previous
			.then(() => this.applyClientAreaChange(reportingConnectorId, privateId, areaId, joined, graceMs))
			.catch(err => {
				this.logger.error(`Failed to apply area report for ${privateId} (${areaId}, joined=${joined}): ${err}`);
			});
		this.areaReportQueues.set(privateId, current);
		await current;
		// Only the LAST link cleans up, or a still-queued follow-up would lose its
		// predecessor and race the very report it is chained behind.
		if(this.areaReportQueues.get(privateId) === current) {
			this.areaReportQueues.delete(privateId);
		}
	}

	/** One area report, applied. Never call directly — go through `handleClientAreaChanged`, which serializes. */
	private async applyClientAreaChange(reportingConnectorId: string | number, privateId: string, areaId: string | number, joined: boolean, graceMs: number) {
		const auth = await this.getUserAuth(privateId);
		if(!auth) return;
		// String-compared, not `===`: connector ids may be numeric, and this
		// record's copy can arrive as a string off a redis hash. A false NEGATIVE
		// here silently drops a legitimate report, which is the worse failure.
		if(String(auth.connectorId) !== String(reportingConnectorId)) return;
		// One atomic row per area, never a whole-list rewrite. `String(areaId)`
		// because the index is a hash keyed by area id: a redis hash field is a
		// string no matter what was written, so normalizing HERE keeps a
		// numeric area id from reading back as a number in memory mode and a
		// string on redis (the same String()-on-write rule the store applies to
		// connector ids).
		if(joined) {
			await this.stateStore.addClientArea(privateId, String(areaId), graceMs > 0 ? graceMs : 0);
			// COMPENSATING DELETE — the index write above is unconditional, and
			// that is a real difference from the `updateUserAuth` write it
			// replaced, which no-opped when the record was gone. A report that
			// read the record while it was still there and then lost the rest of
			// its way to a purge (`removeUserAuth` — the DEFAULT disconnect path,
			// since grace is opt-in) would otherwise leave a row behind that
			// NOTHING can ever delete: its owning record is gone, privateIds are
			// never reused so nothing addresses it again, and there is no sweeper.
			// That is one immortal row per racing disconnect — unbounded growth
			// for the life of the deployment, not a one-off.
			//
			// Re-reading the record AFTER the write closes it exactly: if the
			// record is gone, this report is writing for a session that no longer
			// exists, so drop everything it left. Correct on both backings and,
			// on redis, cluster-wide — the record is SHARED state there, so a
			// report landing on a different gate than the one that purged still
			// sees the purge. Ordering-safe: the per-privateId queue means no
			// other report for this client can interleave, and no LATER
			// legitimate row can exist for a privateId whose record is gone.
			//
			// Cost is one extra record read on JOIN reports only (a leave is an
			// `hdel`/Map-delete that cannot create a key). Paid deliberately: the
			// leak it closes is unbounded, and this is the only mechanism that is
			// exact rather than eventual (the redis TTL backstop in
			// `RedisGateStateStore.addClientArea` covers just the case this
			// cannot — a gate that dies between the two calls).
			if(!(await this.getUserAuth(privateId))) {
				await this.stateStore.clearClientAreas(privateId);
				this.logger.debug(`Dropped client→area rows for ${privateId}: its auth record was purged while an area report was being applied.`);
				return;
			}
		} else {
			await this.stateStore.removeClientArea(privateId, String(areaId));
		}
		// The connector has now spoken for this client, so the hand-off window
		// `consumeRejoinAreas` parked (see `heldGraceMs`) has served its purpose:
		// this report and the ones after it ARE the answer it was standing in for.
		// Left in place it would keep sizing the hold off a seat the client has
		// since left.
		if(Number(auth.heldGraceMs ?? 0) > 0) {
			await this.updateUserAuth(privateId, 'heldGraceMs', 0);
		}
	}

	/**
	 * How long to hold this client's auth record: the LONGEST seat-hold among
	 * the areas it is actually in. A client in no seat-holding area is purged
	 * immediately — the pre-existing default-off behaviour.
	 *
	 * `heldGraceMs` covers the ONE gap where the index lies: the seat has been
	 * granted with the held list on it (so `consumeRejoinAreas` emptied the
	 * index) but the client has not connected yet, so no join report has
	 * refilled it. Reading 0 there purges a record whose areas are still holding
	 * its seat — and, with sequential public ids, hands its publicId to the next
	 * player, who is then rebound straight into that seat.
	 *
	 * ASYNC now: the area list is read from the store's client→area index, not
	 * off the record. `heldGraceMs` stays a record scalar, so a caller that has
	 * a record but no index rows still gets its parked window.
	 *
	 * Numeric coercion, not a bare read: a redis hash gives every field back as
	 * a string.
	 */
	private async holdMsFor(auth?: AuthenticatedGateClient<Auth>): Promise<number> {
		const handedOff = Number(auth?.heldGraceMs ?? 0);
		const parked = Number.isFinite(handedOff) ? handedOff : 0;
		if(!auth?.privateId) return parked;
		const areas = await this.stateStore.getClientAreas(auth.privateId);
		const fromAreas = areas.reduce((max, a) => (Number(a.graceMs) > max ? Number(a.graceMs) : max), 0);
		return Math.max(fromAreas, parked);
	}

	/**
	 * The server-derived rejoin list: the areas this client holds a seat in.
	 *
	 * Only areas that actually hold a seat (graceMs > 0) qualify, and only when
	 * the client is returning into the same game. PURE — deriving the list must
	 * not consume it, because a reservation can still fail after this point and
	 * `reserveSeat`'s catch deliberately leaves the grace hold armed so a retry
	 * inside the window can succeed. That retry needs the list to still be there,
	 * and `holdMsFor` (which reads the same index) needs it to keep sizing the
	 * live grace window. Consumption is `consumeRejoinAreas`, on hand-off only.
	 *
	 * ASYNC now (the list comes from the store's client→area index). Callers on
	 * the `reserveSeat` path must keep awaiting it BEFORE the gameId write —
	 * see that method.
	 */
	private async rejoinAreaIdsFor(auth: AuthenticatedGateClient<Auth> | undefined, gameId: GameId): Promise<Array<string | number>> {
		if(!auth || String(auth.gameId) !== String(gameId)) return [];
		const areas = await this.stateStore.getClientAreas(auth.privateId);
		return areas.filter(a => Number(a.graceMs) > 0).map(a => a.areaId);
	}

	/**
	 * The hand-off: a connector GRANTED the seat, so the list this gate just
	 * handed over is consumed exactly once.
	 *
	 * Clearing here is what keeps a DEAD area from lingering forever: a destroyed
	 * area never sends a leave report, so the list is rebuilt from the join
	 * reports that follow rather than pruned. It is deliberately NOT done at
	 * derivation time — a rejected reservation is not a hand-off, and consuming
	 * on rejection both hands the retry an empty list (silently dropping every
	 * area `getInitialAreas` does not return) and zeroes `holdMsFor`, which would
	 * make the NEXT disconnect purge the record immediately mid-grace-window.
	 */
	private async consumeRejoinAreas(auth: AuthenticatedGateClient<Auth> | undefined, handedOffGraceMs: number) {
		if(!auth) return;
		// Awaited, not floating: an unhandled rejection out of a redis write is
		// process-fatal on modern Node. A failed write is survivable (the areas
		// are re-derived from the join reports that follow), so log and continue.
		try {
			// The window this hand-off carried, PARKED rather than lost: the areas
			// keep holding the seat until the client reconnects and its connector
			// reports where it landed, and until then the index is empty and
			// `holdMsFor` would otherwise read 0 — purging the record, and with
			// sequential ids handing its publicId (which an area still holds a seat
			// under) to the next player. Released by the first area report.
			//
			// BEFORE the index clear below, and written whenever it CHANGES —
			// including down to the 0 of a hand-off that carried nothing. A second
			// hand-off arriving before any area report finds the index already
			// empty, and skipping the write there would leave the first hand-off's
			// window parked on a record that has since been re-scoped to another
			// game. (Equal-value writes are skipped: an ordinary first join hands
			// off nothing and has nothing parked, so it costs no redis write.)
			if(Number(auth.heldGraceMs ?? 0) !== handedOffGraceMs) {
				auth.heldGraceMs = handedOffGraceMs;
				await this.updateUserAuth(auth.privateId, 'heldGraceMs', handedOffGraceMs);
			}
			// UNCONDITIONAL, where the record-backed version first read the list
			// and skipped an empty one to save a write: clearing the index is a
			// single `del` of a key that a first-time join never created, so the
			// read that guard needed now costs MORE than the write it saved (and
			// `del` on a missing key is a no-op on both backings).
			await this.stateStore.clearClientAreas(auth.privateId);
		} catch (err) {
			this.logger.error(`Failed to clear held areas for ${auth.privateId}: ${err}`);
		}
	}

	/**
	 * Hold `privateId`'s auth record instead of purging it immediately: arms
	 * (or re-arms) a grace timer that, unless cancelled first by a successful
	 * re-reservation (`clearPendingReconnect`), purges for real via the same
	 * `removeUserAuth` eviction the old immediate path used. A client whose
	 * seat reservation was still in flight (initiated but never confirmed via
	 * `FINISHED_RESERVATION`) when the grace window elapses has that
	 * reservation defensively cleared too — mirroring the `removeSeat`
	 * pairing the `CLIENT_DISCONNECTED` branch already does for an immediate
	 * disconnect.
	 *
	 * Exception: if `privateId` is in `reservationsInFlight` — a `reserveSeat`
	 * call is awaiting its connector's response for it right now — the purge
	 * (and the defensive `removeSeat`) is deferred instead of run here.
	 * Purging mid-flight would delete the auth record that in-progress
	 * reservation still expects to update on success; see `reserveSeat` for
	 * how the deferral is resolved once that reservation settles either way.
	 */
	private async graceHold(privateId: string, connectorId: string | number, holdMs: number) {
		// Reconnect grace is OPT-IN (off by default). With no positive grace, purge
		// the auth record IMMEDIATELY instead of holding the seat for a reconnect
		// window — the original pre-failover behavior. Mirrors the timer body below.
		//
		// The purge is also what keeps the client→area index from going stale:
		// `removeUserAuth` deletes the auth record AND clears that client's index
		// rows (they are separate state now, so it clears them explicitly), so an
		// area that evicted this client WITHOUT sending a leave report — exactly
		// what a connector teardown does, and what a destroyed area can never
		// send — cannot leave a corpse behind. The index never outlives the
		// record it belongs to.
		if(holdMs <= 0) {
			if(this.reservationsInFlight.has(privateId)) {
				this.deferredPurges.add(privateId);
				return;
			}
			// NO CLAIM TO WIN on this branch — it never armed a hold, so there is
			// no record for a losing caller to be told about. It is also the
			// DEFAULT path (grace is opt-in per area) and now runs once per client
			// of a dead connector on a redis gate, so the fire-and-forget purge
			// needs the same rejection guard the `onConnectionClosed` listener
			// already carries: one redis error out of an un-`catch`ed floating
			// promise is a process-fatal unhandled rejection on modern Node, and
			// this one fires N times per connector death.
			void this.removeUserAuth(privateId).catch(err => {
				this.logger.error(`Failed to purge auth for ${privateId} after an ungraced disconnect: ${err}`);
			});
			if(privateId in this.reservingSeats) {
				await this.removeSeat(privateId);
			}
			return;
		}
		const existing = this.holdTimers.get(privateId);
		if(existing) clearTimeout(existing);
		const timer = setTimeout(async () => {
			// EVERYTHING this callback does is inside the try: `setTimeout`
			// DISCARDS the promise an async callback returns, so a rejection
			// escaping here is an UNHANDLED REJECTION — process-fatal on Node >= 15,
			// and nothing in this package installs a `process.on('unhandledRejection')`
			// net. On a redis-backed gate the body below is up to EIGHT network
			// round trips (the claim, then `removeUserAuth`'s record read, identity
			// release, id release, ownership delete, connector-index delete, record
			// delete, area-index delete), so one failover / READONLY replica /
			// CLUSTERDOWN / dropped connection landing while any grace window
			// expires would take the whole gate — and every connected player —
			// down with it. That is the ORDINARY outcome of a redis blip, not an
			// exotic one, and it became reachable the moment redis mode started
			// arming holds at all.
			//
			// Same guard, same reason, as the `holdMs <= 0` branch above and the
			// `onConnectionClosed` listener: this path is strictly more exposed
			// than either, because it runs unattended long after the call that
			// armed it returned.
			try {
			this.holdTimers.delete(privateId);
			// Read SYNCHRONOUSLY at timer fire, exactly as the pre-store version
			// did: the store write below is an await on a redis-backed gate, and
			// re-checking after it would let a reservation that started during
			// that gap flip the decision this timer already made.
			const reservationInFlight = this.reservationsInFlight.has(privateId);
			// THE CLAIM. Clearing the hold is an atomic delete that reports
			// whether THIS timer is the one that removed it, and everything below
			// is gated on winning — because in redis mode the hold is SHARED and
			// this timer is not the only thing that can end the window:
			//
			//  - the reconnect landed on ANOTHER GATE. That gate cleared the hold
			//    through the same op (`clearPendingReconnect`) and re-seated the
			//    player; it cannot reach into this process to cancel this timer.
			//    Ungated, this fires later and runs `removeUserAuth` on a record
			//    that is now a LIVE, re-seated session — the player is silently
			//    logged out mid-game and their publicId recycled under them.
			//  - ANOTHER GATE'S TIMER for the same hold. Every gate that saw the
			//    connector die armed one (they all enumerate the same shared
			//    `conn:{id}` set), so concurrent expiry is the NORMAL outcome, not
			//    a rare race. `removeUserAuth` is a multi-round-trip read-then-write
			//    with no CAS, so both would sail past its own `if(!auth)` guard and
			//    both would release the sequential publicId — the same id pushed
			//    onto the shared pool twice, handed to two different players — and
			//    `onRemovedAuth` would fire twice for one session.
			//
			// One `DEL`/`Map.delete` decides it: exactly one caller is told it won,
			// so exactly one purge, one release, one `onRemovedAuth`. The deferral
			// below is inside the claim too — a deferred purge is still THIS gate
			// taking ownership of ending the window.
			const claimed = await this.stateStore.clearGraceHold(privateId);
			if(!claimed) {
				// Someone else ended this window: a reconnect that landed elsewhere,
				// or another gate's timer that got here first. Either way this
				// privateId is no longer ours to purge.
				this.logger.debug(`Grace timer for ${privateId} lost the hold claim — the window was already ended elsewhere; not purging.`);
				// ...but this gate's OWN seat bookkeeping is still its to release,
				// and losing the claim is the one exit from this timer that no
				// other path cleans up after: no purge runs, `removeUserAuth`
				// never fires, and `seatReservationTimeout` has no default — so an
				// entry here plus its `pendingSeatReservations` increment would
				// permanently inflate that connector's load in `getBestConnector`.
				// LOCAL-ONLY, deliberately (never `removeSeat`): the cluster-visible
				// `reserving:{privateId}` record now belongs to whoever won the
				// claim — usually the gate that just re-seated this very client —
				// and deleting it would blind that gate's mid-join takeover
				// eviction. See `releaseLocalSeat`.
				this.releaseLocalSeat(privateId);
				return;
			}
			// RE-READ, and only ever to NARROW the snapshot above. The snapshot is
			// what stops a reservation that STARTED during the claim's round trip
			// from flipping this timer's decision (the reason it is read
			// synchronously); this second read stops the opposite case, which the
			// snapshot alone gets wrong: a reservation that SETTLED during that
			// round trip. Deferring to it then hands the purge to a call that has
			// already run its own reconciliation and cleared its guard — so
			// nobody honours it, and the auth record, its publicId ownership row
			// and its sequential id are left addressed by nothing and deleted by
			// nothing (`p:` carries no TTL and privateIds are never reused).
			// `&&`, deliberately: still-in-flight defers exactly as before, and
			// no case that used to purge now defers.
			//
			// UNSTATED DEPENDENCY, named here: this re-read is only trustworthy
			// because of an invariant nothing else in this file asserts. Every
			// successful reservation's last act before it removes itself from
			// `reservationsInFlight` is a store round trip issued AFTER this
			// timer's own claim (`clearGraceHold`) was sent, and RESP replies on
			// the wrapper's single connection are strictly FIFO. Both calls
			// share that one connection, so the claim's reply can only come back
			// after the reply to any reservation call that was already queued
			// ahead of it — a reservation cannot have cleared the flag by the
			// time this line runs unless its closing call was sent, and
			// answered, before the claim's. That ordering is what makes a
			// PREMATURE purge (racing ahead of a reservation's own cleanup)
			// unreachable; nothing here enforces it directly, it falls out of
			// the wrapper never opening a second connection.
			if(reservationInFlight && this.reservationsInFlight.has(privateId)) {
				this.deferredPurges.add(privateId);
				return;
			}
			await this.removeUserAuth(privateId);
			if(privateId in this.reservingSeats) {
				await this.removeSeat(privateId);
			}
			} catch (err) {
				this.logger.error(`Grace-hold expiry for ${privateId} failed: ${err}`);
			}
		}, holdMs);
		timer.unref?.();
		this.holdTimers.set(privateId, timer);
		// The timer is registered BEFORE this await so the hold is never
		// observably half-armed from this process' point of view (a purge racing
		// the store write still finds a timer to clear).
		await this.stateStore.setGraceHold(privateId, connectorId, holdMs);
	}

	/**
	 * Cancels `privateId`'s grace hold, if any — called once a re-reservation
	 * succeeds.
	 *
	 * Returns whether this call is the one that ENDED the window (the store's
	 * atomic claim). Callers here do not need the answer — a reconnect proceeds
	 * either way — but it is returned rather than swallowed because this is the
	 * SAME claim the expiry timer gates its purge on, and the win/lose
	 * accounting has to be single-sourced: winning here is precisely what makes
	 * the arming gate's timer lose, which is what stops it purging a session
	 * that has since re-seated (possibly on a different gate entirely).
	 */
	private async clearPendingReconnect(privateId: string): Promise<boolean> {
		const timer = this.holdTimers.get(privateId);
		if(timer) {
			clearTimeout(timer);
			this.holdTimers.delete(privateId);
		}
		// UNCONDITIONAL, deliberately — not gated on this gate owning the timer.
		// In redis mode the hold record is cluster-visible: the gate that arms a
		// hold and the gate that lands the reconnect can be different processes,
		// and only the second one gets here. Gating on the local timer would
		// leave the record behind on every cross-gate reconnect, so a later
		// `reserveSeat` would still read a `previousConnectorId` for a client
		// that is long since re-seated — and, worse, would leave the arming
		// gate's timer holding a claim it could still win. A `del` of a missing
		// key is a no-op on both backings.
		return this.stateStore.clearGraceHold(privateId);
	}

	/**
	 * Drops every pending grace timer outright — no purge callbacks. Used by
	 * `close()` so a torn-down gate can't fire a late `removeUserAuth`/`removeSeat`
	 * minutes after shutdown. Idempotent — safe to call with nothing pending.
	 *
	 * TIMERS ONLY: the store's hold records are deliberately left alone. This is
	 * a teardown, not a reconciliation — `close()` does not purge auth records
	 * either, and in redis mode the records may belong to sessions another gate
	 * can still legitimately resume. A hold whose owning gate is gone for good is
	 * bounded by the redis-mode TTL backstop (`setGraceHold`), which exists for
	 * exactly this case; in memory mode the store dies with the gate.
	 */
	private clearAllPendingReconnects() {
		this.holdTimers.forEach(timer => clearTimeout(timer));
		this.holdTimers.clear();
	}

	/**
	 * A connector died: every client currently seated on it is unreachable, but
	 * may reconnect through a NEW connector within its own hold window
	 * (`holdMsFor`) — see `reserveSeat`'s same-connector-if-healthy-else-best-
	 * available selection. Grace-hold their auth instead of purging it outright.
	 *
	 * Enumerated from the store's connector→clients index on BOTH backings
	 * (maintained by `reserveSeat`'s seat grant and released by
	 * `removeUserAuth`/a re-seat/`resumeSession`'s retire), not by scanning
	 * `authenticatedClients`. On memory mode that index is the same information
	 * the scan produced; on redis it is information the gate simply did not have
	 * before — the redis branch had no connectorId index at all, so a connector
	 * death grace-held NOBODY and every client on it was silently dropped to an
	 * immediate purge on its own next signal. That is a redis-mode behaviour FIX,
	 * not a memory-mode change.
	 */
	private async handleConnectorClosed(d: { id: string | number }) {
		// Snapshotted BEFORE any hold is armed, so a purge triggered by an
		// earlier client's own `graceHold` (immediate-purge branch, which
		// removes that client from this very index) cannot make the iteration
		// skip a later one.
		const privateIds = await this.stateStore.getConnectorClients(d.id);
		// SIZED IN PARALLEL, not one client at a time. Each `holdMsFor` is a
		// store read (a network round-trip on a redis gate), and arming is what
		// starts the reconnect window: run sequentially, the LAST client of a
		// busy connector gets its hold armed N round-trips after the first, and
		// a client that reconnects inside that skew finds no hold and is treated
		// as a fresh join. The reads are independent, so nothing is serialized by
		// doing them together.
		const sized = await Promise.all(privateIds.map(async (privateId) => {
			const auth = await this.getUserAuth(privateId);
			return { privateId, auth, holdMs: auth ? await this.holdMsFor(auth) : 0 };
		}));
		for(const { privateId, auth, holdMs } of sized) {
			// String-compared, never `===` (the store normalizes ids to strings
			// on write, and a connector id may be numeric). A stale index entry —
			// a record already gone, or one that has since re-seated elsewhere —
			// is dropped rather than held: holding it would arm a window for a
			// connector this client is not on, and leaving it would keep a dead
			// privateId in the index forever.
			if(!auth || String(auth.connectorId) !== String(d.id)) {
				await this.stateStore.removeClientFromConnector(d.id, privateId);
				continue;
			}
			await this.graceHold(privateId, d.id, holdMs);
		}
	}

	/**
	 * Master's cluster-wide fan-out of a connector topology change
	 * (MASTER_GATE_PROTOCOLS.CONNECTOR_CHANGE). 'draining' marks the
	 * connector unavailable for new/re seats (see getAvailableConnectors);
	 * 'removed' clears that mark — the connector's own actual disconnect
	 * (handleConnectorClosed, via onConnectionClosed) is what grace-holds its
	 * clients, so this only needs to keep drainingConnectors from leaking.
	 * 'added' needs no gate-side bookkeeping — a newly-registered connector
	 * is already discoverable via serverConnections once its own registration
	 * handshake completes.
	 */
	private handleConnectorChange(payload: ConnectorChangePayload) {
		if(payload.op === 'draining') {
			this.drainingConnectors.add(payload.connectorId);
		} else if(payload.op === 'removed') {
			this.drainingConnectors.delete(payload.connectorId);
		}
	}

	/**
	 * A client disconnected from a connector that is itself still healthy —
	 * it may simply be reconnecting (flaky network, tab refresh, page
	 * navigation). Grace-hold rather than purge immediately. Returns whether
	 * an auth record existed, preserving the exact truthy-return contract
	 * `removeUserAuth` used to provide here, so the CLIENT_DISCONNECTED
	 * caller's connector-clientCount decrement is unaffected.
	 */
	private async handleClientDisconnected(privateId: string, deliberate?: boolean): Promise<boolean> {
		const auth = await this.getUserAuth(privateId);
		if(!auth) return false;
		if(deliberate) {
			// The client deliberately left (close()/clearGame() → connector set
			// `deliberate`): no reconnect is coming, so purge auth NOW rather
			// than holding the seat for the grace window. Cancel any hold that a
			// prior disconnect signal may have armed for this privateId so it
			// can't fire a late purge after the record is already gone.
			await this.clearPendingReconnect(privateId);
			await this.removeUserAuth(privateId);
			return true;
		}
		// != null, not truthiness — a numeric connector id of 0 is valid and
		// must not be treated the same as "no connector" (this codebase has
		// been bitten by exactly this falsy-id class of bug before).
		if(auth.connectorId != null) {
			await this.graceHold(privateId, auth.connectorId, await this.holdMsFor(auth));
		} else {
			// No connector to hold a reconnect against (shouldn't happen in
			// practice — CLIENT_DISCONNECTED only fires for a privateId a
			// connector actually had seated) — fall back to the old immediate purge.
			await this.removeUserAuth(privateId);
		}
		return true;
	}

	/**
	 * ASYNC now: the recycled-id pool is a store op (an atomic `LPOP` on a
	 * redis gate — see `unusedSequentialIds`). Its only caller, `checkAuth`, is
	 * already async.
	 *
	 * `!= null`, not truthiness, on the acquired id: `0` is a legitimate pooled
	 * id (`removeUserAuth` releases `parseInt(publicId)` for whatever a record
	 * carried) and a truthy test would silently discard it and mint a fresh one,
	 * leaking that id out of the pool forever. This is the ACQUIRE half of the
	 * pool guard; the two RELEASE halves (`removeUserAuth`, `resumeSession`) are
	 * guarded symmetrically on the same rule.
	 *
	 * BOTH HALVES ARE THE STORE'S NOW — recycle first (`acquireSequentialId`, an
	 * atomic `LPOP`), then MINT (`nextSequentialId`, an atomic `INCR`). The mint
	 * used to be this process' own `lastPublicIdSeq++`, which made the pool
	 * shared and safe while the mint was neither: two gates with a cold pool
	 * both minted 1, and publicId is the id the WHOLE CLUSTER keys a player by.
	 * The shared ownership index can only point at one of the two, so
	 * `getClientByPublicId` — reached straight off the wire by
	 * PEER_CONNECTION_REQUEST and REDIRECT_MESSAGE_TO_CLIENT — resolved one
	 * player's traffic to the OTHER player. Silent cross-player delivery, not a
	 * documentation footnote.
	 *
	 * `lastPublicIdSeq` below survives as the MEMORY-MODE BACKING of that mint
	 * (the same wrapping pattern as `authenticatedClients`/`identityMap`/
	 * `unusedSequentialIds`): `MemoryGateStateStore` increments it through an
	 * accessor, so a single-process gate mints exactly as it always did, and a
	 * redis gate leaves it at 1 forever and uses the shared `seqCounter` key.
	 */
	private async makePublicId() {
		if(this.config.options.useSequentialPublicIds) {
			const recycled = await this.stateStore.acquireSequentialId();
			if(recycled != null) {
				return recycled;
			} else {
				/*
				todo: reset if id is too high
				if(this.lastPublicIdSeq > 65534) {
						this.lastPublicIdSeq = 1;
				}*/
				return this.stateStore.nextSequentialId();
			}
		}
		return makeId(5);
	}

	public async checkAuth(opts: any, req: http.IncomingMessage) : Promise<AuthenticatedGateClient> {
		const v = await this.onAuthenticate(opts, req);
		if(!v) {
			throw new Error(`onAuthenticate must return a truthy value if authenticated.`)
		}

		// The privateId is the client's BEARER credential for its seat and its
		// connector socket — 128 bits from the CSPRNG, never Math.random.
		const privateId = randomBytes(16).toString('base64url');
		const publicId = await this.makePublicId();
		const c =  {
			time: Date.now(),
			privateId,
			publicId,
			auth: v,
		}

		await this.setUserAuth(privateId, c);
		if(this.config.options.authenticationTimeout) {
			this.authenticationTimeouts[privateId] = setTimeout(async () => {
				delete this.authenticationTimeouts[privateId];
				const auth = await this.getUserAuth(privateId);
				// `== null`, not truthiness — the same falsy-id rule
				// `handleClientDisconnected`, `makePublicId` and `reserveSeat`'s
				// `isRejoin` already follow. Connector ids can be `0`, so
				// `!auth.connectorId` read a client that IS seated as "never got
				// a seat" and purged the record out from under a live session.
				// Memory mode only, at the time: a redis hash handed back `"0"`,
				// which is truthy — the two backings disagreed about whether the
				// client survives its own authentication window. The value is
				// normalized to a string now (`STRING_NORMALIZED_AUTH_PROPS`),
				// which fixes the live path on its own; this guard is what keeps
				// the purge correct for any record that carries a raw id anyway.
				if(auth && auth.connectorId == null) {
					this.removeUserAuth(auth.privateId)
				}
			}, this.config.options.authenticationTimeout);
		}
		return c
	}

	public getAvailableConnectors() {
		return Object.values(this.serverConnections).filter(v => {
      if(v.type !== CONNECTOR) return false;
      // A draining connector is never "available" — this keeps
      // getHealthyConnectorById (below) from ever re-seating a reconnecting
      // client onto its OWN previous connector once it's draining. See the
      // matching check in getAvailableConnectorsWithArea for getBestConnector's
      // fresh-join / fallback path.
      if(this.drainingConnectors.has(v.id)) return false;
			const { clientCount, maxClients } = v.heartbeats.lastState || {};
      if(maxClients === undefined || maxClients === -1) return true;
			return Number.isInteger(clientCount) && clientCount + (this.pendingSeatReservations[v.id] ?? 0) < maxClients;
		});
	}

	public getBestConnector(forGame?: string | number, forArea?: string) : RegisteredServerData<ConnectorServerConfigOptions> {
		let least = 999999;
		//todo: add game specific connector selection, or gather them based on selected connectors for all the areas in the game.
		// let game = this.games.find(g => g.id === forGame);
		
		let server : RegisteredServerData<ConnectorServerConfigOptions> = null;
		let available = this.getAvailableConnectorsWithArea(forArea);
		if(!available.length) {
			available = this.getAvailableConnectors();
		}
		available.forEach(v => {
			const computedClientCount = v.heartbeats.lastState.clientCount + (this.pendingSeatReservations[v.id] ?? 0);
			if(computedClientCount < least) {
				least = computedClientCount;
				server = v;
			}
		});
		return server;
	}

	/**
	 * The client's previous connector, if it's still registered and has capacity
	 * — else null.
	 *
	 * String-compared, not `===`: the id reaching here comes from the state
	 * store (`reserveSeat`'s `getGraceHold`), which normalizes ids to strings on
	 * write on both backings, while `serverConnections` keys them however the
	 * connector registered (numeric ids are legal). A raw `===` silently misses
	 * every numeric-id connector, and the failure is invisible — the client is
	 * just quietly re-seated somewhere else by the least-loaded fallback.
	 */
	private getHealthyConnectorById(id: string | number) : RegisteredServerData<ConnectorServerConfigOptions> {
		return this.getAvailableConnectors().find(c => String(c.id) === String(id)) ?? null;
	}

	/**
	 * A seat reservation is in flight for `privateId` on `connector`.
	 *
	 * TWO records, deliberately:
	 *  - `reservingSeats` — the LOCAL half, and the only one that can exist:
	 *    it holds the connector's REGISTERED SERVER RECORD, which is a live
	 *    process-local object (`serverConnections` entry, heartbeat state and
	 *    all) that no store could carry. `removeSeat` needs it to decrement
	 *    `pendingSeatReservations`, and `getBestConnector` needs that count.
	 *    It is IN-FLIGHT-BOUNDED — one entry per reservation currently awaiting
	 *    a connector's RESERVE_SEAT response, cleared by `FINISHED_RESERVATION`,
	 *    by the failure path, by `seatReservationTimeout`, or by a grace purge —
	 *    so it satisfies the memory-bounded rule without moving.
	 *  - `stateStore.setReservingSeat` — the CLUSTER-VISIBLE half: just the
	 *    connector id, which is all another gate needs. `resumeSession` reads it
	 *    to evict a MID-JOIN session (seat requested, `connectorId` not yet
	 *    written) — and in a multi-gate deployment the session being taken over
	 *    is usually mid-join on a DIFFERENT gate, where the local object above
	 *    holds nothing at all.
	 */
	private async initSeat(privateId: string, connector: RegisteredServerData<ConnectorServerConfigOptions>) {
		this.reservingSeats[privateId] = connector;
		// PER-CONNECTOR LOAD HEURISTIC — deliberately per-gate, in memory, and
		// NOT moved to the store. It biases `getBestConnector`/
		// `getAvailableConnectors` so a burst of joins on one gate doesn't all
		// pile onto the connector whose last heartbeat looked emptiest; it is
		// not correctness state, and nothing reads it to decide whether a seat
		// exists. A cross-gate count would be more accurate for exactly one
		// heartbeat interval — every connector's real `clientCount` arrives on
		// the next heartbeat and overrides it anyway, so staleness self-corrects
		// by design. Sharing it would buy a rounding-error's worth of accuracy
		// at the cost of a write on the hottest path and a permanent
		// reconciliation problem (a gate that dies mid-reservation leaves a
		// count nobody can decrement).
		if(!(connector.id in this.pendingSeatReservations)) {
			this.pendingSeatReservations[connector.id] = 0;
		}
		this.pendingSeatReservations[connector.id]++;
		await this.stateStore.setReservingSeat(privateId, connector.id);
	}

	/**
	 * The PROCESS-LOCAL half of `removeSeat`: this gate's in-flight-reservation
	 * record, its seat timeout and the per-connector pending count — and
	 * NOTHING cluster-visible.
	 *
	 * Split out because there is one caller that must release its own
	 * bookkeeping WITHOUT touching the store: a grace timer that LOST the hold
	 * claim (see `graceHold`). That gate no longer owns the session — some other
	 * gate's reconnect or expiry does — so deleting the shared
	 * `reserving:{privateId}` record would clear the WINNER's in-flight
	 * reservation out from under it (that record is what a mid-join takeover
	 * eviction reads). But its own `reservingSeats` entry and the
	 * `pendingSeatReservations` increment that came with it are this process'
	 * alone, and nothing else will ever clear them: the loser runs no purge, and
	 * `seatReservationTimeout` has no default, so the entry and the inflated
	 * count would sit in `getBestConnector`'s load heuristic forever, steering
	 * every future join away from a connector that is not actually loaded.
	 */
	private releaseLocalSeat(privateId: string) {
		if(privateId in this.seatReservationTimeouts) {
			clearTimeout(this.seatReservationTimeouts[privateId]);
			delete this.seatReservationTimeouts[privateId];
		}
		const connector = this.reservingSeats[privateId];
		if(connector) {
			this.pendingSeatReservations[connector.id] = this.pendingSeatReservations[connector.id]
				? this.pendingSeatReservations[connector.id] - 1
				: 0
			delete this.reservingSeats[privateId];
			if(!this.pendingSeatReservations[connector.id]) {
				delete this.pendingSeatReservations[connector.id]
			}
		}
	}

	private async removeSeat(privateId: string) {
		this.releaseLocalSeat(privateId);
		// OUTSIDE the `if(connector)` guard, unlike the local bookkeeping above:
		// the store record can exist when the local one does not (another gate
		// initiated the reservation, or this gate restarted mid-flight), and
		// that is precisely the entry that must not be left behind. A `del` of a
		// missing key is a no-op on both backings.
		await this.stateStore.clearReservingSeat(privateId);
	}

	public async reserveSeat(privateId: string, gameId: GameId, options?: any) : Promise<ReservedSeat & {
    connector: RegisteredServerData<ConnectorServerConfigOptions>,
    gameType: string | number
   }> {
		// `options` here is the RAW CLIENT JOIN BODY — handleJoinGame hands the
		// request data straight in, and it becomes the SEAT's options, which the
		// connector merges into what master and every area's onJoinRequest see.
		// `rejoin`/`rejoinAreaIds` are SERVER-derived (this method sets the flag
		// below and derives the held-area list from the gate's own client->area
		// index), so strip anything the client asserted at this boundary — before
		// it can become seat state. This is the layer that stops a hand-rolled
		// client from ever naming its own rejoin; the gate's own flag is added
		// AFTER this, so it still reaches the seat untouched.
		if(options && typeof options === 'object' && !Array.isArray(options)) {
			const { rejoin: _clientRejoin, rejoinAreaIds: _clientRejoinAreaIds, ...clientSupplied } = options as Record<string, any>;
			options = clientSupplied;
		}
		// Mark the reservation in flight BEFORE the first await. getUserAuth
		// yields on a redis-backed gate, and graceHold's purge timer can fire
		// during any await in this method — while this flag is set the purge
		// DEFERS (see graceHold) instead of deleting the auth record out from
		// under a live reservation. Released once, in the trailing finally, after
		// the success path (which cancels the grace hold) or the failure path
		// (which honors any deferred purge) has reconciled.
		this.reservationsInFlight.add(privateId);
		try {
		const priorAuth = await this.getUserAuth(privateId);
		const { publicId, auth } = priorAuth;
		// Server-derived. The CLIENT never supplies this.
		//
		// Derived BEFORE the gameId write below, deliberately: on a non-redis gate
		// `priorAuth` IS the stored record, so writing gameId first would make
		// rejoinAreaIdsFor's same-game check compare gameId against itself and
		// hand back a held area from a DIFFERENT game. Consumed only once the
		// connector actually grants the seat — see consumeRejoinAreas.
		//
		// AWAITED now (the list comes from the store's client→area index). The
		// ORDER is what matters and is unchanged: derive here, before the gameId
		// write, and consume only on grant.
		const rejoinAreaIds = await this.rejoinAreaIdsFor(priorAuth, gameId);
		// The hold window those areas represent, captured HERE for the same
		// reason the list is: once the gameId write below lands, a non-redis gate
		// can no longer tell whose areas these were. Parked on the record by
		// `consumeRejoinAreas` so the gap between granting the seat and the
		// client's first area report is not a purge window (see `holdMsFor`).
		const handedOffGraceMs = rejoinAreaIds.length ? await this.holdMsFor(priorAuth) : 0;
		// A pending grace hold (armed by graceHold, via handleConnectorClosed or
		// handleClientDisconnected) is the authoritative "this privateId was
		// disconnected and may be reconnecting" signal — mirrors AreaServer's
		// own `detachedClients.has(playerId)` rejoin check. A merely-populated
		// auth.connectorId would NOT be enough on its own: an already-connected
		// client calling reserveSeat again (e.g. to join a second game without
		// re-authenticating) would otherwise be misflagged as a rejoin.
		//
		// Known race (not fixed here, self-limiting): a client can reconnect to
		// the gate FASTER than the gate can detect its old connector died.
		// Connector death is heartbeat-bound (BaseServer's doHeartbeat tick plus
		// a full pong-wait, both HEARTBEAT_TIMEOUT=15000ms, so up to ~15-30s
		// worst case) — until handleConnectorClosed fires, no grace hold
		// exists for this privateId yet, so isRejoin below is false and
		// this call is treated as a fresh join (e.g. it falls through to
		// whatever a fresh join does downstream, such as a game's
		// getInitialAreas hook running its normal matchmaking instead of any
		// rejoin-aware area selection it might otherwise do for
		// options.rejoin/previousConnectorId). This can only misfire inside the
		// detection window the race itself depends on, and it under-fires
		// rather than over-fires (worst case: treated as a fresh join, never a
		// wrongful rejoin) — so it is self-limiting. The area layer is
		// unaffected either way: AreaServer.handleClientJoin decides
		// rejoin-vs-fresh off its OWN `detachedClients.has(playerId)`, not off
		// anything derived here.
		//
		// A held seat counts too: the gate's own area index says this client is
		// still seated somewhere, which is authoritative even when the grace hold
		// has not been armed yet (the heartbeat-bound detection window described
		// above) — that is precisely the race the server-derived list closes.
		//
		// Two sources, in priority order, because a rejoin can arrive under a
		// privateId that never had a hold of its OWN. An identity-matched resume
		// retires the held privateId (clearing ITS grace hold) and
		// hands this fresh privateId the held record — including its
		// `connectorId`. Without the second source that whole path reads
		// `previousConnectorId === undefined`: a reloading player is re-seated by
		// least-loaded pick instead of onto the connector that was holding them,
		// the documented `rejoin: true` + `previousConnectorId` pair is
		// half-empty for exactly that case, and resumeSession's adopted
		// `connectorId` is dead state nothing reads. Gated on the held-seat
		// signal (`rejoinAreaIds.length > 0`) rather than on `connectorId` alone,
		// preserving the rule above: a merely-populated connectorId must never by
		// itself make a live client's second reserveSeat look like a rejoin.
		//
		// READ FROM THE STORE, not from this gate's own timer map: in redis mode
		// the gate that armed the hold and the gate the client reconnects to are
		// routinely different processes, and the local map would answer
		// "no hold" for every cross-gate reconnect — turning a genuine rejoin
		// into a fresh join (least-loaded re-seat, empty `previousConnectorId`,
		// the whole documented rejoin contract half-empty). In memory mode this
		// is the same entry the map used to carry, written and cleared at
		// exactly the same points. The id comes back String-normalized (the
		// store normalizes on write, on both backings) — `getHealthyConnectorById`
		// compares stringified, so a numeric connector id still resolves.
		const previousConnectorId = (await this.stateStore.getGraceHold(privateId))?.connectorId
			?? (rejoinAreaIds.length > 0 ? priorAuth?.connectorId : undefined);
		const isRejoin = previousConnectorId != null || rejoinAreaIds.length > 0; // != null, not truthiness — connector ids can be 0
		// Reconnect-aware selection: prefer the client's previous connector if
		// it's still healthy/has capacity (same seat, same connector). Falls
		// back to the normal best-connector pick for a fresh join, or when the
		// previous connector is gone/full.
		const connector = (isRejoin && this.getHealthyConnectorById(previousConnectorId)) || this.getBestConnector(gameId);
		if(!connector) throw new Error(`No available connector servers.`);
		if(privateId in this.authenticationTimeouts) {
			clearTimeout(this.authenticationTimeouts[privateId]);
			delete this.authenticationTimeouts[privateId];
		}
		// A re-reservation (the client had a live grace hold, whether we landed
		// back on the same connector or fell over to a new one) TELLS the
		// connector/area this is a rejoin — visibility only. The area rebinds off
		// its OWN detachedClients set (AreaServer.handleClientJoin), never off
		// this flag, so a missing/forged `rejoin` can't change who gets rebound.
		const seatOptions = isRejoin
			? { ...(options || {}), rejoin: true, previousConnectorId }
			: options;
		try {
			await this.initSeat(privateId, connector);
			// The gate authoritatively knows every game's type (the master pushes
			// ADD_GAME on registration). Pass it along so the connector can seat a
			// client for an instance-only game whose areas don't exist yet.
			const gameType = this.games.find(g => g.id === gameId)?.type;
			// The connector round-trip (up to seatReservationTimeout, ~15s) is
			// covered by the method-wide reservationsInFlight guard armed above,
			// so a grace purge during it defers rather than deleting the record.
			const response = await this.requestServer(connector.id, [GATE_CONNECTOR_REQUEST_PROTOCOLS.RESERVE_SEAT, { privateId, publicId, auth, gameId, gameType, options: seatOptions, rejoinAreaIds }]);
			// THE SEAT WAS GRANTED — this is the one moment a client becomes
			// "seated on connector X", so it is where the connector→clients index
			// is maintained. `handleConnectorClosed` enumerates that index to
			// grace-hold a dead connector's clients; before it existed, the redis
			// branch could not enumerate at all and held nobody.
			//
			// A RE-SEAT moves the entry rather than adding a second one: the same
			// privateId reconnecting onto a different connector (failover, or the
			// least-loaded fallback) would otherwise stay listed under its old,
			// dead connector forever — a row nothing can ever delete, since
			// `removeUserAuth` only knows the connector the record CURRENTLY names.
			if(priorAuth?.connectorId != null && String(priorAuth.connectorId) !== String(connector.id)) {
				await this.stateStore.removeClientFromConnector(priorAuth.connectorId, privateId);
			}
			await this.stateStore.addClientToConnector(connector.id, privateId);
			await this.updateUserAuth(privateId, 'connectorId', connector.id);
			// Scope this client's area list to the game it just got a seat in — a
			// rejoin is only ever offered back into the same game. Written HERE,
			// after the grant, rather than before the request: the area-join
			// reports that rebuild the area index can only arrive once the client is
			// actually seated, so this always lands first, and a reservation that
			// never landed does not re-scope the record to a game the client
			// never entered.
			await this.updateUserAuth(privateId, 'gameId', gameId);
			// The hand-off completed: the seat was granted with `rejoinAreaIds`
			// on it, so the held list is consumed now — not back when it was
			// derived, which would have stranded a retry after a failed grant.
			await this.consumeRejoinAreas(priorAuth, handedOffGraceMs);
			// The re-reservation landed — cancel any grace hold so its timer
			// doesn't purge the just-restored auth record later, and discard any
			// purge graceHold deferred while this reservation was in flight (the
			// record survived untouched, so there's nothing left to purge).
			await this.clearPendingReconnect(privateId);
			this.deferredPurges.delete(privateId);
			if(this.config.options.seatReservationTimeout) {
				this.seatReservationTimeouts[privateId] = setTimeout(async () => {
					delete this.seatReservationTimeouts[privateId];
					if(privateId in this.reservingSeats) {
						await this.removeSeat(privateId);
					}
				}, this.config.options.seatReservationTimeout);
			}
			return { privateId, publicId, connector, gameId: response.gameId, gameType: response.gameType }
		} catch (err) {
			console.error(err);
			// only want to remove the seat if something fails, otherwise we remove it later when we get the message from the connector
			await this.removeSeat(privateId);
			// rethrow the error so it's handled correctly — the deferred-purge
			// reconciliation lives in the OUTER catch below, which covers this
			// throw and every other one.
			throw err;
		}
		} catch (err) {
			// graceHold deferred a purge for this privateId while this
			// reservation was in flight, and the reservation just failed —
			// honor that purge now so a client that never reconnects doesn't
			// keep an immortal auth record (an ordinary failure with no
			// deferred purge leaves any still-armed grace hold untouched, so a
			// legitimate retry within the grace window is unaffected).
			//
			// OUTSIDE the inner try, deliberately: that try only wraps the
			// connector round trip, while the deferral can be armed at ANY await
			// in this method — including before the inner try is entered. The
			// throw that proves it is the most ordinary failure this method has,
			// `No available connector servers.` (a full or empty cluster), which
			// escaped with the deferral intact and nobody left to honour it: the
			// auth record, its publicId ownership row and its sequential id were
			// then addressed by nothing and deleted by nothing, since `p:` has no
			// TTL and privateIds are never reused. A re-entrant `delete` is why
			// this is safe to have inherited from the inner catch: whoever
			// removes the entry first is the one that purges, exactly once.
			if(this.deferredPurges.delete(privateId)) {
				await this.removeUserAuth(privateId);
			}
			throw err;
		} finally {
			// Released exactly once, after success/failure reconciliation: a
			// purge deferred while this was held is discarded on success (record
			// survived) or honored on failure (see the catch) before we get here.
			this.reservationsInFlight.delete(privateId);
		}
	}

	public override async close() {
		this.clearAllPendingReconnects();
		await super.close();
		if(!this.httpServer) return;
		return new Promise<void>((resolve) => {
			this.httpServer.close(resolve);
			delete this.httpServer;
		})
	}

	/**
	 * PARITY (Task 5): the memory branch this replaced resolved by SCANNING
	 * `authenticatedClients` for `v.publicId === pubId` — a strict comparison,
	 * so a numeric `publicId` never matched a string `pubId`. Both backings now
	 * answer from a real ownership index keyed by `String(publicId)`, i.e. the
	 * redis branch's always-stringified semantics. That makes memory mode
	 * strictly MORE permissive than before (`'5'` now finds publicId `5`), never
	 * less — the only records it can no longer find are ones written straight
	 * into `authenticatedClients` without going through `setUserAuth`/
	 * `updateUserAuth`, which never take publicId ownership on either backing.
	 */
	public async getClientByPublicId(pubId: string) : Promise<AuthenticatedGateClient> {
		const privateId = await this.stateStore.getPublicIdOwner(pubId);
		if(privateId === undefined) return undefined;
		return this.getUserAuth(privateId);
	}

	/** The identity path configured by `gameId`'s game file, pushed by master with ADD_GAME. */
	private identityPathFor(gameId: GameId): string | undefined {
		return this.games.find(g => String(g.id) === String(gameId))?.authIdentityPath;
	}

	/**
	 * The `identityMap` key for a verified identity value IN A GAME.
	 *
	 * Scoped by game because `authIdentityPath` is declared per game: the same
	 * VALUE under two games means two unrelated players (see `identityMap`).
	 * The gameId is percent-encoded so a gameId containing the separator can't
	 * collide with a different game/identity pair that concatenates the same.
	 */
	private identityKeyFor(gameId: GameId, identity: string): string {
		return `${encodeURIComponent(String(gameId))}:${identity}`;
	}

	/**
	 * Point `identityKey` at this record and record the key ON the record, so
	 * `removeUserAuth` can release it later without re-deriving it.
	 *
	 * THE ONLY WAY a record may take ownership of an index key — both the
	 * first-join path (`handleJoinGame`) and the resume path (`resumeSession`)
	 * go through here, because both can run against a record that ALREADY owns
	 * a key and neither may leave the old one behind.
	 *
	 * ONE RECORD OWNS ONE KEY: a record whose previous key is different (a
	 * session joining a second game — with a fresh `authenticate()` or, per
	 * `reserveSeat`'s note, on the same privateId) releases the old one. Its
	 * held areas and `gameId` are being re-scoped to the new game anyway, so the old
	 * key describes a seat this record no longer holds. Leaving it behind is not
	 * merely a leak: a later `authenticate()` + join into THAT game would match
	 * the stale entry and "take over" this very session, evicting the player's
	 * own live transport in the game they are actually in.
	 *
	 * Guarded on still being the owner, mirroring `removeUserAuth`: another
	 * session may have re-pointed the old key at itself already, and this record
	 * must not clear an entry it no longer owns.
	 */
	private async claimIdentity(auth: AuthenticatedGateClient<Auth>, identityKey: string) {
		const previous = (await this.getUserAuth(auth.privateId))?.identityKey ?? auth.identityKey;
		if(previous && previous !== identityKey && (await this.getIdentityOwner(previous)) === auth.privateId) {
			await this.clearIdentity(previous);
		}
		auth.identityKey = identityKey;
		await this.updateUserAuth(auth.privateId, 'identityKey', identityKey);
		await this.setIdentity(identityKey, auth.privateId);
	}

	// The identity index, on whichever backing this gate has. In memory mode the
	// store WRAPS this gate's own `identityMap` Map, so `gate.identityMap` still
	// reads and writes the exact same entries these three go through.
	//
	// PARITY (Task 5): a redis-backed gate no longer ALSO mirrors every write
	// into the process-local `identityMap` Map. That mirror was write-only —
	// the redis branch's own reads went to redis and never consulted it, and
	// nothing outside these three methods reads the Map — so dropping it
	// changes no lookup; it just stops a redis-mode gate from accumulating a
	// per-identity entry in process memory forever, which is what the plan's
	// governing memory-bounded rule asks for anyway.
	//
	// All three are deliberately NOT `async` — same one-microtask reason as
	// `getUserAuth` above.

	private setIdentity(identityKey: string, privateId: string) : Promise<void> {
		return this.stateStore.setIdentityOwner(identityKey, privateId);
	}

	private getIdentityOwner(identityKey: string): Promise<string | undefined> {
		return this.stateStore.getIdentityOwner(identityKey);
	}

	private clearIdentity(identityKey: string) : Promise<void> {
		return this.stateStore.clearIdentityOwner(identityKey);
	}

	/**
	 * Adopt a held session's in-game identity onto this fresh authentication:
	 * same `publicId` (so every held seat, entity and channel keyed by it still
	 * matches), same area list, same game, same connector — new `privateId`,
	 * because that is a per-session secret and must not be recycled.
	 *
	 * Adopting the publicId is the whole point. `checkAuth` mints a fresh
	 * publicId as well as a fresh privateId, and EVERYTHING holding a
	 * disconnected player's seat — `AreaServer.detachedClients`, the game
	 * process' clients, channel membership, entities — is keyed by publicId. An
	 * identity match that only routed the player back to the right AREA would
	 * still deliver them as a stranger, and the held state would expire anyway.
	 *
	 * Works against a LIVE session as well as a held one. A second login under
	 * the same identity while the first tab's socket is still open is a
	 * DUPLICATE LOGIN, and it TAKES OVER: the old transport is dropped at its
	 * connector (`DISCONNECT_CLIENT`) before this record adopts the publicId, so
	 * two open sockets never share one in-game id with ambiguous area/channel
	 * routing. There is no refuse-the-second-login mode.
	 *
	 * Returns the adopted publicId, or `undefined` when there is nothing to
	 * resume. Retires the previous record afterwards so one identity owns
	 * exactly one session.
	 */
	private async resumeSession(fresh: AuthenticatedGateClient<Auth>, identityKey: string): Promise<string | number | undefined> {
		const heldPrivateId = await this.getIdentityOwner(identityKey);
		if(!heldPrivateId || heldPrivateId === fresh.privateId) return undefined;
		const held = await this.getUserAuth(heldPrivateId);
		if(!held) {
			// The session that owned this identity is already purged (grace expired,
			// deliberate leave) and only the index entry is left. Drop the stale
			// entry — the caller registers the fresh privateId right after.
			await this.clearIdentity(identityKey);
			return undefined;
		}

		// The publicId this fresh authenticate minted and is about to abandon.
		// Captured here because the adopt below overwrites it; RELEASED after the
		// adopt, see the release block for why the two cannot be merged.
		const abandonedPublicId = fresh.publicId;

		// Is the session we are about to displace still LIVE? A grace-HELD one has
		// a grace hold and no transport behind it — that is the ordinary rejoin,
		// and there is nothing to evict. Anything else with a connector attached
		// is a DUPLICATE LOGIN: a second tab authenticating under the same
		// identity while the first tab's socket is still open.
		// Read BEFORE the retire below, which clears the grace hold AND the
		// reserving-seat record.
		//
		// BOTH reads go through the store, not this gate's own maps: the session
		// being displaced can belong to ANOTHER gate in a redis deployment, and
		// that gate's timer map and `reservingSeats` object are invisible here.
		// Reading locally would call every cross-gate rejoin a takeover (evicting
		// at a connector that has nothing) and miss every cross-gate mid-join
		// eviction (letting an already-reserved seat admit a socket onto the
		// publicId this record just adopted). In memory mode these are the same
		// entries the local maps held, written and cleared at the same points.
		//
		// `held.connectorId` is only written once RESERVE_SEAT has been GRANTED,
		// so a session that is MID-JOIN — seat requested, connectorId not yet
		// written, socket not yet opened — would look like it has no transport to
		// drop. It still has a reserved SEAT, and a seat admits a socket all by
		// itself (ConnectorServer.onValidateClient), so fall back to the connector
		// the seat is being reserved on. Gate→connector requests to one connector
		// are published on a single ordered channel, so a DISCONNECT_CLIENT issued
		// after an in-flight RESERVE_SEAT is processed after it, and deletes the
		// seat that request created. `!= null`, not truthiness — connector id 0 is
		// a valid connector.
		const evictAtConnectorId = held.connectorId != null
			? held.connectorId
			: await this.stateStore.getReservingSeat(heldPrivateId);
		const takingOverLiveSession = evictAtConnectorId != null && !(await this.stateStore.getGraceHold(heldPrivateId));

		// Retire the old record WITHOUT going through removeUserAuth. That path
		// would (a) push the held publicId onto unusedSequentialIds — handing the
		// id the fresh record now owns to the NEXT player, so two live clients
		// share one in-game id — (b) clear the identity entry we are about to
		// re-point, and (c) fire onRemovedAuth for a session that is continuing,
		// not ending. Everything else removeUserAuth cleans up is done by hand
		// here instead.
		await this.clearPendingReconnect(heldPrivateId);
		if(heldPrivateId in this.authenticationTimeouts) {
			clearTimeout(this.authenticationTimeouts[heldPrivateId]);
			delete this.authenticationTimeouts[heldPrivateId];
		}
		// UNCONDITIONAL on the store side (`removeSeat` clears the store record
		// outside its own local guard): the held session's reservation may have
		// been initiated by another gate, so the local object can be empty while
		// the cluster-visible record is not — and that record is exactly what the
		// mid-join eviction above just read.
		await this.removeSeat(heldPrivateId);
		// One call for both backings — in memory mode the store deletes out of
		// this gate's own `authenticatedClients`, which is what the direct
		// `delete` here used to do. The held record's publicId ownership entry is
		// deliberately NOT released: the fresh record is about to ADOPT that
		// publicId, and the `setUserAuth` at the end of this method re-points the
		// entry at the fresh privateId (see the comment there).
		await this.stateStore.deleteAuthRecord(heldPrivateId);

		// A duplicate login TAKES OVER: drop the old transport before this record
		// adopts the publicId, so two open sockets never share one in-game id.
		//
		// Deliberately AFTER the retire above and BEFORE the adopt below. The
		// connector's removeClient fires its removed-client path SYNCHRONOUSLY, so
		// its CLIENT_DISCONNECTED for `heldPrivateId` reaches this gate before the
		// request below resolves. Evicting first (with the record still present)
		// would let that report grace-hold — or, with grace off, `removeUserAuth` —
		// the very record whose publicId is being adopted: the id would be pushed
		// back onto `unusedSequentialIds` for the next player, `onRemovedAuth`
		// would fire for a session that is continuing, and on redis the purge's
		// `pubIdMap` delete would race the `setUserAuth` below. Retiring first
		// makes that report a guaranteed no-op instead of a race.
		if(takingOverLiveSession) {
			try {
				const evicted = await this.requestServer(evictAtConnectorId, [GATE_CONNECTOR_REQUEST_PROTOCOLS.DISCONNECT_CLIENT, heldPrivateId]);
				if(!evicted) {
					// The connector had neither a live client nor a reserved seat for
					// this privateId. Benign (the session was already gone, or another
					// gate in a multi-gate deployment is the one holding it), but it is
					// the ONLY signal that the eviction found nothing — don't discard it.
					this.logger.debug(`Takeover: connector ${evictAtConnectorId} had nothing to evict for ${heldPrivateId}.`);
				}
			} catch (err) {
				// The connector is gone, unreachable or mid-teardown — the session it
				// held is already unreachable too, so the takeover proceeds either
				// way. An eviction failure must never fail the login, but it is never
				// swallowed silently either.
				this.logger.warn(`Takeover: could not evict ${heldPrivateId} at connector ${evictAtConnectorId}: ${err instanceof Error ? err.message : String(err)}`);
			}
		}

		// Adopt only what the held record actually HAS. An `undefined` here is not
		// a harmless copy of "nothing": this record is written straight back with
		// `setUserAuth`, and on a redis-backed gate that is
		// `RedisClientWrapper.hset(key, object)` → node-redis `pushObject` →
		// RESP2 `encodeCommand`, which accepts only strings and Buffers and
		// THROWS `TypeError: Invalid argument type` on anything else. That throw
		// escapes `resumeSession` AFTER the held record has already been deleted
		// and its transport evicted: the join 400s, the held seat and its rejoin
		// list are gone, and the identity entry is left pointing at a privateId
		// that no longer exists.
		//
		// Ordinary flows reach it. `gameId`/`connectorId` are unwritten when
		// `reserveSeat` threw (e.g. no available connector), and a player who
		// authenticates and calls `joinGame` (which registers the identity BEFORE
		// `reserveSeat`) and then closes the tab before the websocket opens holds
		// no areas at all — the index is filled ONLY by the connector's join
		// reports, which a socket that never opened cannot send.
		fresh.publicId = held.publicId;
		// The held areas are RE-KEYED, not copied onto the record: they are rows
		// in the store's client→area index under the OLD privateId, and this
		// session continues under a new one. Read → re-add under the fresh id →
		// clear the old id, in that order, so a failure between the two leaves
		// the rows readable under one id or both, never neither. An empty held
		// index writes nothing and clears nothing — the fresh id simply has no
		// rows, which is exactly what `holdMsFor`/`rejoinAreaIdsFor` read as "no
		// seat held" (no `areas: []` placeholder needed any more; there is no
		// record field to leave `undefined`).
		const heldAreas = await this.stateStore.getClientAreas(heldPrivateId);
		for(const { areaId, graceMs } of heldAreas) {
			await this.stateStore.addClientArea(fresh.privateId, String(areaId), Number(graceMs));
		}
		await this.stateStore.clearClientAreas(heldPrivateId);
		if(held.gameId != null) fresh.gameId = held.gameId;
		if(held.connectorId != null) fresh.connectorId = held.connectorId;
		if(held.heldGraceMs != null) fresh.heldGraceMs = held.heldGraceMs;
		// The connector→clients index is RE-KEYED for the same reason the area
		// rows are: this session continues under a new privateId, and the index
		// entry belongs to the session, not to the id. Without this the retired
		// privateId sits in that connector's set forever (nothing addresses it
		// again — `removeUserAuth` never runs for a hand-retired record — so on
		// redis it is an immortal set member, one per resume), and the CONTINUING
		// session is absent from it: when that connector dies,
		// `handleConnectorClosed` grace-holds a privateId that no longer exists
		// and silently skips the live one, which is then purged on its own next
		// disconnect signal instead of held. Mirrors the area re-key above:
		// add under the new id, then drop the old, so a failure between the two
		// leaves the client listed once or twice, never zero times.
		if(held.connectorId != null) {
			await this.stateStore.addClientToConnector(held.connectorId, fresh.privateId);
			await this.stateStore.removeClientFromConnector(held.connectorId, heldPrivateId);
		}

		// Release the freshly-minted publicId this authentication just abandoned,
		// exactly the way removeUserAuth releases the publicId of a record it
		// evicts — or this hand-retire path is asymmetric with it and leaks:
		//  - the sequential pool leaks an id per resume; and
		//  - on redis, `checkAuth`'s setUserAuth already wrote
		//    `pubIdMap[abandoned] = fresh.privateId`. Nothing else ever deletes it
		//    (the setUserAuth below writes the ADOPTED key, it does not move the
		//    old one), so the stale key would resolve getClientByPublicId for the
		//    abandoned id to the live resumed session — and, once that session
		//    ends, to a dead privateId, forever.
		//
		// Deliberately AFTER the adopt: releasing while `fresh` still CARRIED the
		// abandoned id opens a window where a concurrent checkAuth pops it from
		// the pool and mints a second record with it, while this record still
		// reports it — making getClientByPublicId (a first-match scan) resolve to
		// whichever it hits first. The eviction round-trip above sits inside that
		// window, so it is an await wide, not an instant.
		if(String(abandonedPublicId) !== String(held.publicId)) {
			// Same guarded release as `removeUserAuth`'s — see the note there for
			// why an unusable id must never reach the pool.
			if(this.config.options?.useSequentialPublicIds) {
				const released = parseInt(abandonedPublicId as string);
				if(Number.isFinite(released)) {
					await this.stateStore.releaseSequentialId(released);
				}
			}
			// UNCONDITIONAL now, where only the redis branch used to do it —
			// `checkAuth`'s `setUserAuth` took publicId ownership for the
			// abandoned id on BOTH backings (it always did on redis; memory mode
			// used to have no ownership index at all and answered by scanning),
			// so memory mode would otherwise keep resolving the abandoned id to
			// this very much alive resumed session, and then to a dead privateId
			// once it ends — exactly the leak the comment above describes for
			// redis.
			try {
				await this.stateStore.deletePublicIdOwner(abandonedPublicId);
			} catch (err) {
				console.error('error removing pubIdMap', err);
			}
		}

		// Persist AFTER the delete: on redis this re-points `pubIdMap[publicId]`
		// at the fresh privateId, and the old record's key is already gone so
		// nothing can resurrect it.
		await this.setUserAuth(fresh.privateId, fresh);
		// Through `claimIdentity`, NOT a bare `setIdentity`: `fresh` is usually a
		// brand-new `checkAuth` record with no key of its own, but it does not
		// have to be. A session that already joined one game and then joins a
		// SECOND one whose held session it resumes arrives here owning the first
		// game's key — and keeping both would hand this one record two index
		// entries, the second of which a later login into the first game would
		// match and "take over", evicting this player's own live transport.
		await this.claimIdentity(fresh, identityKey);
		this.logger.info(`Identity-matched resume: session ${heldPrivateId} → ${fresh.privateId}, keeping publicId ${fresh.publicId} in game ${fresh.gameId}.`);
		return fresh.publicId;
	}

	public override async onServerRequest(server: RegisteredServerData<any>, payload: any): Promise<any> {
		if(server.type === CONNECTOR) {
			switch(payload[0]) {
				case GATE_CONNECTOR_REQUEST_PROTOCOLS.PEER_CONNECTION_REQUEST: {
					const requesterPublicId = payload[1];
					const requestedPublicId = payload[2];
					const handshakeData = payload[3];
					const systemName = payload[4];
					const reqOpts = payload[5];
					/*console.log('GATE PEER_CONNECTION_REQUEST', { 
						requesterPublicId, 
						requestedPublicId, 
						handshakeData, 
						systemName, 
						reqOpts
					 })*/
					const client = await this.getClientByPublicId(requestedPublicId);
					//console.log('GATE PEER_CONNECTION_REQUEST', { client, })
					if(!client) return null;
					//[GATE_CONNECTOR_REQUEST_PROTOCOLS.PEER_CONNECTION_REQUEST, requesterPublicId, requestedPublicId, handshakeData, systemName, requestOptions
					const v = await this.requestServer(client.connectorId, [
						GATE_CONNECTOR_REQUEST_PROTOCOLS.PEER_CONNECTION_REQUEST, 
						client.privateId,
						requesterPublicId,
						handshakeData,
						systemName,
						reqOpts
					])
					//console.log('GOT BACK V:', v);
					return v;
				}
			}
		}
	}

	public addGame(type: string, id: string, clientData: any, meta: any, authIdentityPath?: string) {
		// // console.log('gate add game', { type, id, meta });
		this.games.push({ type, id, meta, clientData, authIdentityPath });
	}
	public removeGame(id: string) {
		const idx = this.games.findIndex(i => i.id ===  id);
		if(idx > -1) {
			this.games.splice(idx, 1);
		}
	}
	
	private updateServerCount(serverId: string | number, count: number) {
		const server = this.serverConnections[serverId];
		if(server?.heartbeats?.lastState && 'clientCount' in server.heartbeats.lastState) {
			server.heartbeats.lastState.clientCount += count;
		}
	}

	override async onServerMessage(server: RegisteredServerData<any>, msg: any) {
		if(server.type === CONNECTOR) {
			switch(msg[0]) {
				case GATE_TO_CONNECTOR_PROTOCOLS.CLIENT_DISCONNECTED: {
					// Grace-hold instead of purging outright — the client may be
					// reconnecting (see handleClientDisconnected/graceHold) —
					// UNLESS msg[2] flags a deliberate leave (client.close()/
					// clearGame()), in which case auth is purged immediately.
					// Returns the same truthy-if-auth-existed signal removeUserAuth
					// used to, so the clientCount bookkeeping below is unaffected.
					const hadAuth = await this.handleClientDisconnected(msg[1], msg[2]);

					if(hadAuth) {
							this.updateServerCount(server.id, -1);
					}
					// The local guard stays (it gates the `pendingSeatReservations`
					// decrement, which must only happen for a seat THIS gate
					// counted), but the cluster-visible record is cleared either
					// way: the reservation may have been initiated on another gate,
					// where this object holds nothing, and that record must not be
					// left depending on `removeUserAuth` running later to clear it —
					// a grace-held client is not purged at all.
					if(msg[1] in this.reservingSeats) {
							await this.removeSeat(msg[1]);
					} else {
							await this.stateStore.clearReservingSeat(msg[1]);
					}
					break;
				}
				case GATE_TO_CONNECTOR_PROTOCOLS.CLIENT_AREA_CHANGED: {
					await this.handleClientAreaChanged(server.id, msg[1], msg[2], msg[3], msg[4]);
					break;
				}
				case GATE_TO_CONNECTOR_PROTOCOLS.FINISHED_RESERVATION:
					// since we're removing the seat we need to increase the clientCount until we get the next heartbeat to override it.
					this.updateServerCount(server.id, 1);
					await this.removeSeat(msg[1])
					break;
				case GATE_TO_CONNECTOR_PROTOCOLS.REDIRECT_MESSAGE_TO_CLIENT: {
					const pubId = msg[1];
					const protocol = msg[2];
					const msgData = msg[3];
					const client = await this.getClientByPublicId(pubId);
					if(!client) return null;
					this.sendToServer(client.connectorId as string | number, [GATE_TO_CONNECTOR_PROTOCOLS.REDIRECT_MESSAGE_TO_CLIENT, protocol, client.privateId, msgData])
					break;
				}
			}
		} else if (server.type === MASTER) {
			switch(msg[0]) {
				case MASTER_GATE_PROTOCOLS.ADD_GAME:
					// [proto, type, id, clientData, meta, authIdentityPath] — APPEND-ONLY.
					this.addGame(msg[1], msg[2], msg[3], msg[4], msg[5])
					break;
				case MASTER_GATE_PROTOCOLS.REMOVE_GAME:
					this.removeGame(msg[1]);
					break;
				case MASTER_GATE_PROTOCOLS.UPDATE_GAME_META_DATA: {
					const game  = this.games.find(i => i.id ===  msg[1]);
					if(game) {
							game.meta = msg[2]
					}
					break;
				}
				case MASTER_GATE_PROTOCOLS.CONNECTOR_CHANGE:
					this.handleConnectorChange(msg[1]);
					break;
			}
		}
	}
}